import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../components/Toast';
import { STAGES, STAGE_COLORS, INSURERS, stageProgress, timeAgo, getClaimWarnings, specialStageTheme } from '../lib/constants';
import ClaimModal from '../components/ClaimModal';
import NewClaimModal from '../components/NewClaimModal';
import ComparePage from './ComparePage';
import NewRequestsPage from './NewRequestsPage';
import InvoiceTab, { TEMPLATE_KEYS, invoicePreviewHtml } from '../components/InvoiceTab';
import CalendarTab from '../components/CalendarTab';
import TodoPanel from '../components/TodoPanel';
import ParkingLot from '../components/ParkingLot';
import { IcoInsurance, IcoContacts, IcoCompare, IcoInvoice, IcoTasks, IcoCalendar, IcoTow, IcoRental, IcoMail } from '../components/Icons';

const IS_DESKTOP = () => window.innerWidth >= 768;


const DEV_ROOM_ID = '00000000-0000-0000-0000-000000000002';
const DEV_CLAIMS = [
  { id: 'dev-1',  room_id: DEV_ROOM_ID, car: '2021 Toyota Camry',     plate: 'ABC1234', color: 'Silver', vin: '1HGBH41JXMN109186', client_name: 'Mike Johnson',   client_phone: '555-100-2000', insurer: 'State Farm',    claim_num: 'SF-88421', adjuster: 'Sarah Lee',    car_in: '2026-06-10', car_out: null,         status: 'Repair & Paint',    notes: 'Front end damage, waiting on hood.',           tags: [{ id: 't1', type: 'rental', label: 'Rental' }],                                   parts: [{ id: 'p1', name: 'Hood', arrived: false }, { id: 'p2', name: 'Bumper Cover', arrived: true }, { id: 'p3', name: 'Headlight Assy', arrived: true }], created_at: '2026-06-10T10:00:00Z', updated_at: '2026-06-21T08:00:00Z', created_by: 'dev' },
  { id: 'dev-2',  room_id: DEV_ROOM_ID, car: '2019 Honda Accord',     plate: 'XYZ9876', color: 'Blue',   vin: '2T1BURHE0JC034228', client_name: 'Lisa Martinez',  client_phone: '555-200-3000', insurer: 'GEICO',         claim_num: 'GC-55210', adjuster: 'Tom Brown',    car_in: '2026-06-12', car_out: null,         status: 'Waiting on Parts',     notes: 'Quarter panel backordered — ETA June 25.',    tags: [{ id: 't2', type: 'rental', label: 'Rental' }],                                   parts: [{ id: 'p4', name: 'Quarter Panel L', arrived: false }, { id: 'p5', name: 'Door Molding', arrived: true }], created_at: '2026-06-12T09:00:00Z', updated_at: '2026-06-20T14:00:00Z', created_by: 'dev' },
  { id: 'dev-3',  room_id: DEV_ROOM_ID, car: '2022 Ford F-150',       plate: 'TRK4421', color: 'Black',  vin: '3VWFE21C04M000001', client_name: 'David Chen',     client_phone: '555-300-4000', insurer: 'Progressive',   claim_num: 'PR-77843', adjuster: 'Amy White',    car_in: '2026-06-14', car_out: null,         status: 'Total Loss',        notes: 'Rear collision, frame check confirmed total.', tags: [{ id: 't3', type: 'tow', label: 'Tow Bill' }],                                    parts: [], created_at: '2026-06-14T11:00:00Z', updated_at: '2026-06-19T16:00:00Z', created_by: 'dev' },
  { id: 'dev-4',  room_id: DEV_ROOM_ID, car: '2020 Chevy Malibu',     plate: 'MLB3310', color: 'White',  vin: '1G1ZD5ST4LF056789', client_name: 'Anna Scott',     client_phone: '555-400-5000', insurer: 'Allstate',      claim_num: 'AL-34991', adjuster: 'James King',   car_in: '2026-06-08', car_out: null,         status: 'Waiting on Approval',  notes: 'Supplement sent June 15, awaiting response.',  tags: [],                                                                                 parts: [{ id: 'p6', name: 'Trunk Lid', arrived: false }], created_at: '2026-06-08T08:00:00Z', updated_at: '2026-06-18T10:00:00Z', created_by: 'dev' },
  { id: 'dev-5',  room_id: DEV_ROOM_ID, car: '2018 BMW 330i',         plate: 'BMW5500', color: 'Grey',   vin: 'WBA8E9G50HNU12345', client_name: 'Robert Hill',    client_phone: '555-500-6000', insurer: 'Chubb',         claim_num: 'CH-10293', adjuster: 'Nina Patel',   car_in: '2026-06-01', car_out: '2026-06-13', status: 'Closed',            notes: '',                                             tags: [],                                                                                 parts: [{ id: 'p7', name: 'Bumper Assy', arrived: true }, { id: 'p8', name: 'Grille', arrived: true }], created_at: '2026-06-01T09:00:00Z', updated_at: '2026-06-13T17:00:00Z', created_by: 'dev' },
  { id: 'dev-6',  room_id: DEV_ROOM_ID, car: '2023 Tesla Model 3',    plate: 'TSL2023', color: 'Red',    vin: '5YJ3E1EA1PF000001', client_name: 'Jessica Park',   client_phone: '555-600-7000', insurer: 'Tesla Insurance', claim_num: 'TI-00441', adjuster: 'Liam Torres',   car_in: '2026-06-17', car_out: null,         status: 'SIU',         notes: 'Flagged for review — prior-damage discrepancy, awaiting SIU clearance.', tags: [{ id: 't4', type: 'rental', label: 'Rental' }],                                  parts: [], created_at: '2026-06-17T10:00:00Z', updated_at: '2026-06-21T09:00:00Z', created_by: 'dev' },
  { id: 'dev-7',  room_id: DEV_ROOM_ID, car: '2017 Nissan Altima',    plate: 'NIS7723', color: 'Red',    vin: '1N4AL3AP5HC120001', client_name: 'Carlos Rivera',  client_phone: '555-700-8000', insurer: 'Farmers',       claim_num: 'FM-29917', adjuster: 'Cora Mills',   car_in: '2026-06-05', car_out: null,         status: 'Supplement Requested',   notes: 'Hidden damage found during disassembly.',     tags: [{ id: 't5', type: 'tow', label: 'Tow Bill' }, { id: 't6', type: 'rental', label: 'Rental' }], parts: [{ id: 'p9', name: 'Radiator', arrived: true }, { id: 'p10', name: 'AC Condenser', arrived: false }, { id: 'p11', name: 'Cooling Fan', arrived: false }], created_at: '2026-06-05T08:00:00Z', updated_at: '2026-06-20T11:00:00Z', created_by: 'dev' },
  { id: 'dev-8',  room_id: DEV_ROOM_ID, car: '2021 Jeep Grand Cherokee', plate: 'JEP8810', color: 'Green', vin: '1C4RJFBG5MC000001', client_name: 'Tanya Brooks', client_phone: '555-800-9000', insurer: 'Liberty Mutual', claim_num: '', adjuster: '',   car_in: '2026-06-18', car_out: null,         status: 'Car In',            notes: 'Hail damage all panels, roof, hood.',         tags: [],                                                                                 parts: [], created_at: '2026-06-18T14:00:00Z', updated_at: '2026-06-21T07:00:00Z', created_by: 'dev' },
  { id: 'dev-9',  room_id: DEV_ROOM_ID, car: '2016 Subaru Outback',   plate: 'SUB1640', color: 'Orange', vin: '4S4BSANC5G3300001', client_name: 'Greg Walsh',     client_phone: '555-900-1000', insurer: 'USAA',          claim_num: 'US-83002', adjuster: 'Mel Ortiz',    car_in: '2026-06-03', car_out: null,         status: 'Dismantle',         notes: 'Full front clip replacement.',                tags: [{ id: 't7', type: 'tow', label: 'Tow Bill' }],                                   parts: [{ id: 'p12', name: 'Fender L', arrived: true }, { id: 'p13', name: 'Fender R', arrived: true }, { id: 'p14', name: 'Hood', arrived: false }, { id: 'p15', name: 'Bumper Beam', arrived: true }], created_at: '2026-06-03T09:00:00Z', updated_at: '2026-06-19T13:00:00Z', created_by: 'dev' },
  { id: 'dev-10', room_id: DEV_ROOM_ID, car: '2024 Hyundai Tucson',   plate: 'HYU2400', color: 'White',  vin: '5NMJE3AE0PH000001', client_name: 'Priya Nair',    client_phone: '555-010-1100', insurer: 'Nationwide',    claim_num: 'NW-11203', adjuster: 'Dana Cole',    car_in: '2026-06-16', car_out: null,         status: 'Waiting on Supplement',     notes: 'Waiting on insurer approval for frame repair.', tags: [{ id: 't8', type: 'rental', label: 'Rental' }],                                  parts: [{ id: 'p16', name: 'Front Frame Rail', arrived: false }], created_at: '2026-06-16T11:00:00Z', updated_at: '2026-06-20T15:00:00Z', created_by: 'dev' },
  { id: 'dev-11', room_id: DEV_ROOM_ID, car: '2020 Kia Sorento',      plate: 'KIA2011', color: 'Navy',   vin: '5XYPG4A5XLG000001', client_name: 'Sandra Yoon',   client_phone: '555-111-2200', insurer: 'Travelers',     claim_num: 'TR-40021', adjuster: 'Brian Shaw',   car_in: '2026-06-19', car_out: null,         status: 'Car In',            notes: 'Side impact passenger door and rocker panel.',  tags: [],                                                                                 parts: [], created_at: '2026-06-19T09:00:00Z', updated_at: '2026-06-21T10:00:00Z', created_by: 'dev' },
  { id: 'dev-12', room_id: DEV_ROOM_ID, car: '2015 Dodge Charger',    plate: 'CHG1500', color: 'Black',  vin: '2C3CDXBG5FH000001', client_name: 'Marcus Webb',   client_phone: '555-122-3300', insurer: 'Erie Insurance', claim_num: 'ER-88012', adjuster: 'Lisa Dunn',    car_in: '2026-06-11', car_out: null,         status: 'Repair & Paint',    notes: 'Rear quarter and trunk lid repaint.',          tags: [{ id: 't9', type: 'custom', label: 'Rush Job' }],                                 parts: [{ id: 'p17', name: 'Trunk Lid', arrived: true }, { id: 'p18', name: 'Quarter Panel R', arrived: true }, { id: 'p19', name: 'Tail Light', arrived: false }], created_at: '2026-06-11T08:00:00Z', updated_at: '2026-06-21T08:30:00Z', created_by: 'dev' },
  { id: 'dev-13', room_id: DEV_ROOM_ID, car: '2023 Ram 1500',         plate: 'RAM2301', color: 'White',  vin: '1C6SRFFT9PN000001', client_name: 'Tyler Grant',   client_phone: '555-133-4400', insurer: 'Farmers',       claim_num: 'FM-55618', adjuster: 'Cora Mills',   car_in: '2026-06-15', car_out: null,         status: 'Waiting on Parts',     notes: 'OEM bumper assembly on backorder.',            tags: [{ id: 't10', type: 'rental', label: 'Rental' }],                                  parts: [{ id: 'p20', name: 'Front Bumper Assy', arrived: false }, { id: 'p21', name: 'Fog Light L', arrived: true }, { id: 'p22', name: 'Fog Light R', arrived: true }], created_at: '2026-06-15T10:00:00Z', updated_at: '2026-06-20T09:00:00Z', created_by: 'dev' },
  { id: 'dev-14', room_id: DEV_ROOM_ID, car: '2019 Audi Q5',          plate: 'AUD1940', color: 'Grey',   vin: 'WA1BNAFY0KA000001', client_name: 'Elena Moss',    client_phone: '555-144-5500', insurer: 'Chubb',         claim_num: 'CH-29944', adjuster: 'Nina Patel',   car_in: '2026-06-07', car_out: '2026-06-18', status: 'Closed',            notes: 'Front end collision, completed and delivered.', tags: [],                                                                                parts: [{ id: 'p23', name: 'Hood', arrived: true }, { id: 'p24', name: 'Grille', arrived: true }, { id: 'p25', name: 'Headlight R', arrived: true }], created_at: '2026-06-07T09:00:00Z', updated_at: '2026-06-18T16:00:00Z', created_by: 'dev' },
  { id: 'dev-15', room_id: DEV_ROOM_ID, car: '2022 Chevy Silverado',  plate: 'SIL2201', color: 'Red',    vin: '1GCUYDED5NZ000001', client_name: 'Phil Nguyen',   client_phone: '555-155-6600', insurer: 'AAA',           claim_num: 'AA-74310', adjuster: 'Mel Ortiz',    car_in: '2026-06-20', car_out: null,         status: 'Appraiser Inspection',         notes: 'Hail damage roof and hood, appraiser scheduled June 23.', tags: [],                                                                            parts: [], created_at: '2026-06-20T11:00:00Z', updated_at: '2026-06-21T09:30:00Z', created_by: 'dev' },
  { id: 'dev-16', room_id: DEV_ROOM_ID, car: '2021 Mazda CX-5',       plate: 'MZD2100', color: 'Blue',   vin: 'JM3KFBDM0M0000001', client_name: 'Olivia Stone',  client_phone: '555-166-7700', insurer: 'MetLife Auto',  claim_num: 'ML-39201', adjuster: 'Aaron Fox',    car_in: '2026-06-13', car_out: null,         status: 'Dismantle',         notes: 'Full front clip tear down underway.',          tags: [{ id: 't11', type: 'tow', label: 'Tow Bill' }],                                   parts: [{ id: 'p26', name: 'Radiator', arrived: false }, { id: 'p27', name: 'Hood', arrived: false }, { id: 'p28', name: 'Front Bumper', arrived: true }], created_at: '2026-06-13T10:00:00Z', updated_at: '2026-06-20T12:00:00Z', created_by: 'dev' },
  { id: 'dev-17', room_id: DEV_ROOM_ID, car: '2018 Mercedes C300',    plate: 'MRC1800', color: 'Silver', vin: 'WDDWF4KB1JR000001', client_name: 'Natalie Cruz',  client_phone: '555-177-8800', insurer: 'Liberty Mutual', claim_num: 'LM-81004', adjuster: 'Brian Shaw',  car_in: '2026-06-09', car_out: null,         status: 'Supplement Requested',   notes: 'Airbag deployment found during teardown — supplement submitted.', tags: [{ id: 't12', type: 'rental', label: 'Rental' }, { id: 't13', type: 'tow', label: 'Tow Bill' }], parts: [{ id: 'p29', name: 'Airbag Module', arrived: false }, { id: 'p30', name: 'Steering Wheel', arrived: false }, { id: 'p31', name: 'Bumper Cover', arrived: true }], created_at: '2026-06-09T08:00:00Z', updated_at: '2026-06-19T14:00:00Z', created_by: 'dev' },
  { id: 'dev-18', room_id: DEV_ROOM_ID, car: '2020 Volkswagen Tiguan', plate: 'VWT2000', color: 'White',  vin: '3VV2B7AX2LM000001', client_name: 'George Kim',   client_phone: '555-188-9900', insurer: 'USAA',          claim_num: 'US-44209', adjuster: 'Sarah Lee',    car_in: '2026-06-17', car_out: null,         status: 'Waiting on Approval',  notes: 'Estimate submitted, waiting on USAA adjuster review.', tags: [],                                                                           parts: [{ id: 'p32', name: 'Windshield', arrived: false }], created_at: '2026-06-17T09:00:00Z', updated_at: '2026-06-20T16:00:00Z', created_by: 'dev' },
  { id: 'dev-19', room_id: DEV_ROOM_ID, car: '2016 Honda Civic',       plate: 'CIV1610', color: 'Orange', vin: '2HGFC2F50GH000001', client_name: 'Diane Porter',  client_phone: '555-199-0011', insurer: 'Safeco',        claim_num: 'SC-60887', adjuster: 'Tom Brown',    car_in: '2026-06-04', car_out: '2026-06-16', status: 'Closed',            notes: 'Completed on time, client happy.',             tags: [],                                                                                 parts: [{ id: 'p33', name: 'Door Shell L', arrived: true }, { id: 'p34', name: 'Mirror L', arrived: true }], created_at: '2026-06-04T08:00:00Z', updated_at: '2026-06-16T15:00:00Z', created_by: 'dev' },
  { id: 'dev-20', room_id: DEV_ROOM_ID, car: '2025 Ford Bronco',       plate: 'BRN2500', color: 'Yellow', vin: '1FMEE5DPXPLA00001', client_name: 'Jake Owens',    client_phone: '555-200-1122', insurer: 'Progressive',   claim_num: 'PR-92034', adjuster: 'Amy White',    car_in: '2026-06-21', car_out: null,         status: 'Car In',            notes: 'Rollover, extensive roof and A-pillar damage. Total loss evaluation pending.', tags: [{ id: 't14', type: 'tow', label: 'Tow Bill' }], parts: [], created_at: '2026-06-21T07:00:00Z', updated_at: '2026-06-21T11:00:00Z', created_by: 'dev' },
];

const DEV_MEMBERS = [
  { id: 'dev-m1', room_id: DEV_ROOM_ID, user_id: '00000000-0000-0000-0000-000000000001', role: 'admin',  user_name: 'Dev User',      avatar_url: null, joined_at: '2026-06-01T09:00:00Z' },
  { id: 'dev-m2', room_id: DEV_ROOM_ID, user_id: '00000000-0000-0000-0000-000000000003', role: 'member', user_name: 'Alex Rivera',   avatar_url: null, joined_at: '2026-06-05T09:00:00Z' },
  { id: 'dev-m3', room_id: DEV_ROOM_ID, user_id: '00000000-0000-0000-0000-000000000004', role: 'member', user_name: 'Jordan Casey',  avatar_url: null, joined_at: '2026-06-08T09:00:00Z' },
];

const DEV_UPDATES = {
  'dev-1':  [
    { id: 'u1a', claim_id: 'dev-1', _type: 'update', user_name: 'Dev User', text: 'Hood shipped from dealer, should arrive Monday.', created_at: '2026-06-21T08:00:00Z' },
    { id: 'u1b', claim_id: 'dev-1', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Bumper Cover marked arrived', created_at: '2026-06-20T14:30:00Z' },
    { id: 'u1c', claim_id: 'dev-1', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2021 Toyota Camry from "Waiting Parts" → "Repair & Paint"', created_at: '2026-06-19T09:00:00Z' },
  ],
  'dev-2':  [
    { id: 'u2a', claim_id: 'dev-2', _type: 'update', user_name: 'Dev User', text: 'Called GEICO — adjuster confirmed coverage, parts on order.', created_at: '2026-06-20T14:00:00Z' },
    { id: 'u2b', claim_id: 'dev-2', _type: 'log',    user_name: 'Dev User', action: 'tag_added', detail: 'Rental tag added', created_at: '2026-06-19T10:00:00Z' },
  ],
  'dev-3':  [
    { id: 'u3a', claim_id: 'dev-3', _type: 'update', user_name: 'Dev User', text: 'Total loss confirmed by Progressive. Client notified.', created_at: '2026-06-19T16:00:00Z' },
    { id: 'u3b', claim_id: 'dev-3', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2022 Ford F-150 from "Appraiser" → "Total Loss"', created_at: '2026-06-18T11:00:00Z' },
  ],
  'dev-4':  [
    { id: 'u4a', claim_id: 'dev-4', _type: 'update', user_name: 'Dev User', text: 'Supplement emailed to James King at Allstate. Waiting on approval.', created_at: '2026-06-18T10:00:00Z' },
  ],
  'dev-6':  [
    { id: 'u6a', claim_id: 'dev-6', _type: 'update', user_name: 'Dev User', text: 'Tesla parts require OEM only — ordered directly from Tesla dealer.', created_at: '2026-06-21T09:00:00Z' },
    { id: 'u6b', claim_id: 'dev-6', _type: 'log',    user_name: 'Dev User', action: 'tag_added', detail: 'Rental tag added', created_at: '2026-06-18T08:00:00Z' },
  ],
  'dev-7':  [
    { id: 'u7a', claim_id: 'dev-7', _type: 'update', user_name: 'Dev User', text: 'Supplement request submitted — found bent subframe during teardown.', created_at: '2026-06-20T11:00:00Z' },
    { id: 'u7b', claim_id: 'dev-7', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Radiator marked arrived', created_at: '2026-06-15T13:00:00Z' },
    { id: 'u7c', claim_id: 'dev-7', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2017 Nissan Altima from "Dismantle" → "Supplement Req."', created_at: '2026-06-20T10:30:00Z' },
  ],
  'dev-8':  [
    { id: 'u8a', claim_id: 'dev-8', _type: 'update', user_name: 'Dev User', text: 'Vehicle checked in. Hail damage on all horizontal panels. Estimate being written.', created_at: '2026-06-21T07:00:00Z' },
  ],
  'dev-9':  [
    { id: 'u9a', claim_id: 'dev-9', _type: 'update', user_name: 'Dev User', text: 'Teardown complete, all damage documented and sent to USAA.', created_at: '2026-06-19T13:00:00Z' },
    { id: 'u9b', claim_id: 'dev-9', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Fender L marked arrived', created_at: '2026-06-17T10:00:00Z' },
    { id: 'u9c', claim_id: 'dev-9', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Fender R marked arrived', created_at: '2026-06-17T10:05:00Z' },
    { id: 'u9d', claim_id: 'dev-9', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Bumper Beam marked arrived', created_at: '2026-06-18T09:00:00Z' },
  ],
  'dev-10': [
    { id: 'u10a', claim_id: 'dev-10', _type: 'update', user_name: 'Dev User', text: 'Frame scan shows 12mm deviation on left rail. Sent report to Nationwide.', created_at: '2026-06-20T15:00:00Z' },
    { id: 'u10b', claim_id: 'dev-10', _type: 'log',    user_name: 'Dev User', action: 'tag_added', detail: 'Rental tag added', created_at: '2026-06-17T11:00:00Z' },
  ],
  'dev-11': [
    { id: 'u11a', claim_id: 'dev-11', _type: 'update', user_name: 'Dev User', text: 'Vehicle checked in. Photos taken, damage documented.', created_at: '2026-06-21T10:00:00Z' },
    { id: 'u11b', claim_id: 'dev-11', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2020 Kia Sorento from "Car In" → "Appraiser"', created_at: '2026-06-21T10:30:00Z' },
  ],
  'dev-12': [
    { id: 'u12a', claim_id: 'dev-12', _type: 'update', user_name: 'Dev User', text: 'Rush job — client needs car back by Friday. Prioritizing paint.', created_at: '2026-06-21T08:30:00Z' },
    { id: 'u12b', claim_id: 'dev-12', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Trunk Lid marked arrived', created_at: '2026-06-18T09:00:00Z' },
    { id: 'u12c', claim_id: 'dev-12', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Quarter Panel R marked arrived', created_at: '2026-06-18T09:05:00Z' },
  ],
  'dev-13': [
    { id: 'u13a', claim_id: 'dev-13', _type: 'update', user_name: 'Dev User', text: 'Called RAM dealer — bumper assembly ETA is June 26. Waiting.', created_at: '2026-06-20T09:00:00Z' },
    { id: 'u13b', claim_id: 'dev-13', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Fog Light L marked arrived', created_at: '2026-06-17T11:00:00Z' },
    { id: 'u13c', claim_id: 'dev-13', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Fog Light R marked arrived', created_at: '2026-06-17T11:05:00Z' },
  ],
  'dev-14': [
    { id: 'u14a', claim_id: 'dev-14', _type: 'update', user_name: 'Dev User', text: 'Vehicle delivered to client. All repairs signed off.', created_at: '2026-06-18T16:00:00Z' },
    { id: 'u14b', claim_id: 'dev-14', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2019 Audi Q5 from "Repair & Paint" → "Closed"', created_at: '2026-06-18T15:30:00Z' },
  ],
  'dev-15': [
    { id: 'u15a', claim_id: 'dev-15', _type: 'update', user_name: 'Dev User', text: 'AAA appraiser scheduled for Monday June 23. Photos sent over.', created_at: '2026-06-21T09:30:00Z' },
  ],
  'dev-16': [
    { id: 'u16a', claim_id: 'dev-16', _type: 'update', user_name: 'Dev User', text: 'Disassembly 80% done. Found cracked core support, added to estimate.', created_at: '2026-06-20T12:00:00Z' },
    { id: 'u16b', claim_id: 'dev-16', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Front Bumper marked arrived', created_at: '2026-06-19T10:00:00Z' },
    { id: 'u16c', claim_id: 'dev-16', _type: 'log',    user_name: 'Dev User', action: 'tag_added', detail: 'Tow Bill tag added', created_at: '2026-06-13T09:00:00Z' },
  ],
  'dev-17': [
    { id: 'u17a', claim_id: 'dev-17', _type: 'update', user_name: 'Dev User', text: 'Supplement includes airbag module, clock spring, and steering wheel. $4,200 additional.', created_at: '2026-06-19T14:00:00Z' },
    { id: 'u17b', claim_id: 'dev-17', _type: 'log',    user_name: 'Dev User', action: 'part_arrived', detail: 'Bumper Cover marked arrived', created_at: '2026-06-16T10:00:00Z' },
    { id: 'u17c', claim_id: 'dev-17', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2018 Mercedes C300 from "Dismantle" → "Supplement Req."', created_at: '2026-06-19T13:45:00Z' },
  ],
  'dev-18': [
    { id: 'u18a', claim_id: 'dev-18', _type: 'update', user_name: 'Dev User', text: 'Estimate submitted to USAA. Adjuster review expected within 3 business days.', created_at: '2026-06-20T16:00:00Z' },
    { id: 'u18b', claim_id: 'dev-18', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2020 Volkswagen Tiguan from "Car In" → "Waiting Approval"', created_at: '2026-06-18T10:00:00Z' },
  ],
  'dev-19': [
    { id: 'u19a', claim_id: 'dev-19', _type: 'update', user_name: 'Dev User', text: 'Client picked up car. Very satisfied with the work.', created_at: '2026-06-16T15:00:00Z' },
    { id: 'u19b', claim_id: 'dev-19', _type: 'log',    user_name: 'Dev User', action: 'status_change', detail: 'moved 2016 Honda Civic from "Repair & Paint" → "Closed"', created_at: '2026-06-16T14:30:00Z' },
  ],
  'dev-20': [
    { id: 'u20a', claim_id: 'dev-20', _type: 'update', user_name: 'Dev User', text: 'Rollover tow-in. Significant roof crush. Total loss likely — waiting on Progressive to send adjuster.', created_at: '2026-06-21T11:00:00Z' },
    { id: 'u20b', claim_id: 'dev-20', _type: 'log',    user_name: 'Dev User', action: 'tag_added', detail: 'Tow Bill tag added', created_at: '2026-06-21T07:30:00Z' },
  ],
};

export default function FloorPage({ session, room, setRoom, onSwitchRoom }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bgImage, setBgImage] = useState(() => localStorage.getItem(`shoptrack_bg_${room.id}`) || null);
  const [bgBlur, setBgBlur] = useState(() => {
    const saved = localStorage.getItem(`shoptrack_bgblur_${room.id}`);
    return saved !== null ? Number(saved) : 18;
  });
  const [mainTab, setMainTab] = useState('floor');
  const [lotEditMode, setLotEditMode] = useState(false); // lifted up so the mobile tab bar can show an Edit Lot button in place of Tools
  const [filterStage, setFilterStage] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [selectedClaimRoom, setSelectedClaimRoom] = useState(null);
  const [showNewClaim, setShowNewClaim] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [menuScreen, setMenuScreen] = useState('main');
  const [expandedRecentId, setExpandedRecentId] = useState(null);
  const [recentUpdates, setRecentUpdates] = useState({});
  const [showInsurance, setShowInsurance] = useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTodos, setShowTodos] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [stageSince, setStageSince] = useState({}); // claim_id → ISO ts of entering current stage
  const [loadError, setLoadError] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [emailsLatest, setEmailsLatest] = useState(null); // ISO of the newest email thread activity
  const [dueFilter, setDueFilter] = useState(false);
  // Per-device "last opened" map for unread dots — claim_id → ISO timestamp
  const [seenMap, setSeenMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`shoptrack_seen_${room.id}`)) || {}; } catch { return {}; }
  });
  // Card grid vs compact list, remembered per device
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(`shoptrack_view_${room.id}`) || 'grid');
  // Merge claims from all the user's rooms onto this floor (personal, per device)
  const [mergeRooms, setMergeRooms] = useState(() => localStorage.getItem('shoptrack_merge') === '1');
  // Lookup of every room this user belongs to (id → name + their role in it),
  // for labeling merged cards and opening a merged claim against its own room
  const [roomsById, setRoomsById] = useState({ [room.id]: { id: room.id, name: room.name, shop_name: room.shop_name, role: room.role } });

  function openClaim(id) {
    const c = claims.find(x => x.id === id);
    const claimRoom = c && roomsById[c.room_id] ? roomsById[c.room_id] : room;
    setSelectedClaimRoom(claimRoom);
    setSelectedClaimId(id);
    setSeenMap(prev => {
      const next = { ...prev, [id]: new Date().toISOString() };
      try { localStorage.setItem(`shoptrack_seen_${room.id}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function isUnread(claim) {
    const seen = seenMap[claim.id];
    if (!seen) return true; // never opened on this device
    return claim.updated_at && new Date(claim.updated_at) > new Date(seen);
  }

  function toggleViewMode() {
    const v = viewMode === 'grid' ? 'list' : 'grid';
    setViewMode(v);
    try { localStorage.setItem(`shoptrack_view_${room.id}`, v); } catch {}
  }

  const emailsSeenAt = () => localStorage.getItem(`shoptrack_emails_seen_${room.id}`);
  const emailsUnread = emailsLatest && (!emailsSeenAt() || new Date(emailsLatest) > new Date(emailsSeenAt()));
  const unreviewedCount = claims.filter(c => c.unreviewed).length;

  async function loadEmailsLatest() {
    if (room.id === DEV_ROOM_ID) return;
    const { data } = await supabase.from('email_threads').select('last_message_at').eq('room_id', room.id).order('last_message_at', { ascending: false }).limit(1).maybeSingle();
    if (data) setEmailsLatest(data.last_message_at);
  }

  function openEmails() {
    try { localStorage.setItem(`shoptrack_emails_seen_${room.id}`, new Date().toISOString()); } catch {}
    setShowEmails(true);
  }

  // Promise-date helper: 'overdue' | 'due' (within 7 days) | null
  function dueStatus(c) {
    if (!c.car_out || c.status === 'Closed' || c.status === 'Total Loss') return null;
    const days = Math.ceil((new Date(c.car_out + 'T23:59:59') - Date.now()) / 86400000);
    if (days < 0) return 'overdue';
    if (days <= 7) return 'due';
    return null;
  }

  const loadClaims = useCallback(async () => {
    if (room.id === DEV_ROOM_ID) {
      setClaims(DEV_CLAIMS);
      setStageSince({});
      setLoading(false);
      return;
    }
    const roomIds = mergeRooms ? Object.keys(roomsById) : [room.id];
    const claimsQuery = supabase
      .from('claims')
      .select('*, tags(*), parts(*)')
      .order('updated_at', { ascending: false });
    const { data, error } = await (roomIds.length > 1
      ? claimsQuery.in('room_id', roomIds)
      : claimsQuery.eq('room_id', room.id));
    if (data) { setClaims(data); setLoadError(false); }
    if (error) setLoadError(true);
    // When each claim entered its current stage = latest status_change log per claim
    const logsQuery = supabase
      .from('activity_log')
      .select('claim_id, created_at')
      .eq('action', 'status_change')
      .order('created_at', { ascending: false });
    const { data: logs } = await (roomIds.length > 1
      ? logsQuery.in('room_id', roomIds)
      : logsQuery.eq('room_id', room.id));
    if (logs) {
      const map = {};
      for (const l of logs) { if (!map[l.claim_id]) map[l.claim_id] = l.created_at; }
      setStageSince(map);
    }
    setLoading(false);
  }, [room.id, mergeRooms, roomsById]);

  // Build a lookup of every room this user belongs to (id → name + their role),
  // used to label merged cards and to open a merged claim against its own room.
  useEffect(() => {
    if (room.id === DEV_ROOM_ID) return;
    supabase.from('room_members')
      .select('role, rooms(id, name, shop_name)')
      .eq('user_id', session.user.id)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        for (const m of data) {
          if (m.rooms) map[m.rooms.id] = { id: m.rooms.id, name: m.rooms.name, shop_name: m.rooms.shop_name, role: m.role };
        }
        setRoomsById(map);
      });
  }, [room.id, session.user.id]);

  // Keep this user's name + profile picture fresh on their membership row,
  // so the members list can show them to everyone (backfills old members too)
  useEffect(() => {
    if (room.id === DEV_ROOM_ID) return;
    const userName = session.user.user_metadata?.full_name || session.user.email;
    const avatar = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null;
    supabase.from('room_members').update({ user_name: userName, avatar_url: avatar })
      .eq('room_id', room.id).eq('user_id', session.user.id).then(() => {});
  }, [room.id, session.user.id]);

  useEffect(() => {
    loadClaims();
    loadEmailsLatest();
    // When merging, a claim change in ANY of the user's rooms should refresh the
    // floor — so drop the room filter on the claims channel and let loadClaims +
    // RLS scope the result. Single-room mode keeps the tighter filter.
    const claimsBinding = mergeRooms
      ? { event: '*', schema: 'public', table: 'claims' }
      : { event: '*', schema: 'public', table: 'claims', filter: `room_id=eq.${room.id}` };
    const ch = supabase.channel(`room-${room.id}-floor`)
      .on('postgres_changes', claimsBinding, loadClaims)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tags' }, loadClaims)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, loadClaims)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [room.id, mergeRooms, loadClaims]);

  async function loadUpdatesForClaim(claimId) {
    if (recentUpdates[claimId]) return;
    if (room.id === DEV_ROOM_ID) {
      setRecentUpdates(prev => ({ ...prev, [claimId]: DEV_UPDATES[claimId] || [] }));
      return;
    }
    const [{ data: updates }, { data: logs }] = await Promise.all([
      supabase.from('claim_updates').select('*').eq('claim_id', claimId).order('created_at', { ascending: false }).limit(10),
      supabase.from('activity_log').select('*').eq('claim_id', claimId).order('created_at', { ascending: false }).limit(10),
    ]);
    const combined = [
      ...(updates || []).map(u => ({ ...u, _type: 'update' })),
      ...(logs || []).map(l => ({ ...l, _type: 'log' })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10);
    setRecentUpdates(prev => ({ ...prev, [claimId]: combined }));
  }

  function toggleRecentExpand(claimId) {
    if (expandedRecentId === claimId) {
      setExpandedRecentId(null);
    } else {
      setExpandedRecentId(claimId);
      loadUpdatesForClaim(claimId);
    }
  }

  const stageCounts = STAGES.reduce((acc, s) => {
    acc[s] = claims.filter(c => c.status === s).length;
    return acc;
  }, {});

  const dueCount = claims.filter(c => dueStatus(c) === 'due').length;
  const overdueCount = claims.filter(c => dueStatus(c) === 'overdue').length;

  const filtered = claims.filter(c => {
    if (filterStage && c.status !== filterStage) return false;
    // Keep the All view showing the actual floor: closed claims disappear after 7 days
    if (!filterStage && c.status === 'Closed' && (Date.now() - new Date(c.updated_at)) > 7 * 86400000) return false;
    if (dueFilter && !dueStatus(c)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (c.plate || '').toLowerCase().includes(q) ||
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.claim_num || '').toLowerCase().includes(q) ||
        (c.car || '').toLowerCase().includes(q) ||
        (c.adjuster || '').toLowerCase().includes(q) ||
        (c.insurer || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const recentClaims = [...claims]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 20);

  const desktop = IS_DESKTOP();
  const userInitial = (session.user.user_metadata?.full_name || session.user.email || '?')[0].toUpperCase();
  const avatarUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null;

  function openMenu() { setMenuScreen('main'); setMenuOpen(true); }
  function closeMenu() {
    if (menuClosing) return;
    setMenuClosing(true);
    setTimeout(() => { setMenuOpen(false); setMenuClosing(false); }, 300);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', position: 'relative' }}>
      {bgImage && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0,
          minHeight: 'var(--app-height, 100%)',
          ...(bgImage.startsWith('#')
            ? { background: bgImage, filter: `blur(${bgBlur}px)` }
            : { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: `blur(${bgBlur}px)` }),
          transform: 'scale(1.08)',
          pointerEvents: 'none',
        }} />
      )}
      <style>{`
        .claim-card {
          animation: rise 0.22s ease both;
          transition: box-shadow 0.15s, transform 0.1s;
          box-shadow: 0 3px 0 rgba(16,16,20,0.8), 0 7px 12px rgba(0,0,0,0.38);
        }
        .claim-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 0 rgba(16,16,20,0.8), 0 9px 16px rgba(0,0,0,0.45), 0 0 14px rgba(249,115,22,0.22);
        }
        .claim-card:active {
          transform: translateY(3px);
          box-shadow: 0 1px 0 rgba(16,16,20,0.8), 0 3px 6px rgba(0,0,0,0.32);
        }
        .pipe-strip::-webkit-scrollbar { display: none; }
        .tab-scroll::-webkit-scrollbar { display: none; }
        .menu-slide { animation: menuSlideIn 0.45s cubic-bezier(0.32,0.72,0,1) both; }
        .menu-slide.closing { animation: menuSlideOut 0.3s cubic-bezier(0.4,0,1,1) both; }
        @keyframes menuSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes menuSlideOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        .menu-fade { animation: menuFadeIn 0.45s ease both; }
        .menu-fade.closing { animation: menuFadeOut 0.3s ease both; }
        @keyframes menuFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes menuFadeOut { from { opacity: 1; } to { opacity: 0; } }
        .menu-screen-fwd { animation: menuScreenFwd 0.22s ease both; }
        .menu-screen-back { animation: menuScreenBack 0.22s ease both; }
        @keyframes menuScreenFwd { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: none; } }
        @keyframes menuScreenBack { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: none; } }
        .recent-updates { overflow: hidden; animation: expandDown 0.2s ease both; }
        @keyframes expandDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        .menu-row { transition: background 0.12s; }
        .menu-row:hover { background: var(--bg4); }
        .menu-row:active { background: var(--bg4); }
        .menu-group > * + * { box-shadow: inset 0 7px 7px -7px rgba(0,0,0,0.55); }
      `}</style>

      {/* ── CONTENT (above bg) ── */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

      {/* ── HEADER ── */}
      <div style={{ background: bgImage ? 'transparent' : 'var(--bg)', padding: desktop ? '0 32px' : '0 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: desktop ? 70 : 52, padding: desktop ? '12px 0' : '6px 0', position: 'relative' }}>

          {/* Logo — centered on desktop, left on mobile */}
          <div style={{ position: desktop ? 'absolute' : 'relative', left: desktop ? '50%' : 'auto', transform: desktop ? 'translateX(-50%)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {room.logo_url ? (
              <img src={room.logo_url} alt="Shop logo" style={{ height: 'auto', maxHeight: desktop ? 120 : 80, width: 'auto', maxWidth: desktop ? 900 : '90vw', objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 3px 2px rgba(0,0,0,0.6)) drop-shadow(0 7px 10px rgba(0,0,0,0.45))' }} />
            ) : (
              <div className="text-3d" style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 44 : 22, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', lineHeight: 1.05, color: 'var(--text)', textAlign: 'center', maxWidth: desktop ? '56vw' : '72vw' }}>
                {room.shop_name || room.name || 'ShopTrack'}
              </div>
            )}
          </div>

          {/* Left spacer */}
          <div style={{ flex: 1 }} />

          {/* Right: avatar only */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={openMenu} className="raise-3d" style={{
              width: desktop ? 42 : 36, height: desktop ? 42 : 36, borderRadius: '50%',
              background: menuOpen ? 'var(--orange)' : 'var(--bg3)',
              border: menuOpen ? '2px solid var(--orange)' : 'none',
              color: menuOpen ? '#16100b' : 'var(--text)', fontWeight: 700, fontSize: desktop ? 16 : 14,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0,
              overflow: 'hidden', padding: 0,
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : userInitial}
            </button>
          </div>
        </div>
      </div>

      {/* ── TOOLS ROW (desktop only) ── */}
      {desktop && (
        <div style={{ background: bgImage ? 'transparent' : 'var(--bg)', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0, padding: '10px 32px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setShowInsurance(true)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 8, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <IcoInsurance size={20} /> Insurance Contacts
          </button>
          <button onClick={() => setShowContacts(true)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 8, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <IcoContacts size={20} /> Important Contacts
          </button>
          <button onClick={() => setShowCompare(true)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 8, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <IcoCompare size={20} /> Compare Estimates
          </button>
          <button onClick={() => setShowInvoice(true)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 8, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <IcoInvoice size={20} /> New Invoice
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={openEmails} title="Emails" className="raise-3d" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 11px', cursor: 'pointer', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <IcoMail size={22} />
            {emailsUnread && <span className="unread-dot" style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: 'var(--orange)' }} />}
          </button>
          <button onClick={() => setShowRequests(true)} title={unreviewedCount > 0 ? `New Requests (${unreviewedCount})` : 'New Requests'} className="raise-3d" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 11px', cursor: 'pointer', color: 'var(--text2)', transition: 'all 0.15s' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
            {unreviewedCount > 0 && <span className="unread-dot" style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: 'var(--orange)' }} />}
          </button>
          {room.role === 'admin' && (
            <button onClick={() => setShowStats(true)} title="Shop Stats" className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 11px', cursor: 'pointer', color: 'var(--text2)', transition: 'all 0.15s' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="20" x2="5" y2="12"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="19" y1="20" x2="19" y2="9"/></svg>
            </button>
          )}
          <button onClick={() => setShowTodos(true)} title="Shop Tasks" className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 11px', cursor: 'pointer', color: 'var(--text2)', transition: 'all 0.15s' }}><IcoTasks size={22} /></button>
          <button onClick={() => setShowCalendar(true)} title="Calendar" className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg3)', border: 'none', borderRadius: 8, height: 38, padding: '0 11px', cursor: 'pointer', color: 'var(--text2)', transition: 'all 0.15s' }}><IcoCalendar size={22} /></button>
        </div>
      )}

      {/* ── PIPELINE STRIP ── */}
      <div style={{ position: 'relative', zIndex: 5, flexShrink: 0, background: bgImage ? 'transparent' : 'var(--bg)', boxShadow: desktop ? '0 10px 14px -8px rgba(0,0,0,0.55)' : '0 -10px 14px -8px rgba(0,0,0,0.55), 0 10px 14px -8px rgba(0,0,0,0.55)' }}>
        <div className="pipe-strip" style={{ overflowX: 'auto', display: 'flex', gap: desktop ? 8 : 6, padding: desktop ? '10px 32px 12px' : '8px 16px 10px', scrollbarWidth: 'none' }}>
          <PipeBox label="All" count={claims.length} color="var(--text2)" active={filterStage === null} onClick={() => setFilterStage(null)} desktop={desktop} />
          {STAGES.map(stage => (
            <PipeBox key={stage} label={stageShort(stage)} count={stageCounts[stage] || 0} color={STAGE_COLORS[stage]} active={filterStage === stage} onClick={() => setFilterStage(filterStage === stage ? null : stage)} desktop={desktop} />
          ))}
        </div>
        {!desktop && <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 56, background: `linear-gradient(to right, transparent, ${bgImage ? 'rgba(0,0,0,0.3)' : 'var(--bg)'})`, pointerEvents: 'none' }} />}
      </div>

      {/* ── DUE THIS WEEK STRIP ── */}
      {mainTab === 'floor' && (dueCount > 0 || overdueCount > 0) && (
        <button onClick={() => setDueFilter(v => !v)} style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
          padding: desktop ? '8px 32px' : '7px 16px',
          background: dueFilter ? 'rgba(249,115,22,0.14)' : (bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)'),
          border: 'none', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)',
          cursor: 'pointer', fontSize: desktop ? 13 : 12, fontWeight: 700,
          fontFamily: 'var(--font-display)', letterSpacing: '0.5px', textTransform: 'uppercase',
          color: dueFilter ? 'var(--orange)' : 'var(--text2)', width: '100%',
        }}>
          {dueCount > 0 && <span style={{ color: 'var(--yellow)' }}>🕐 {dueCount} due this week</span>}
          {overdueCount > 0 && <span style={{ color: 'var(--red)' }}>⚠ {overdueCount} overdue</span>}
          <span style={{ color: 'var(--text3)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>{dueFilter ? 'showing — tap to clear' : 'tap to show'}</span>
        </button>
      )}

      {/* ── TAB BAR + SEARCH ── */}
      <div style={{ position: 'relative', zIndex: 4, boxShadow: '0 10px 14px -8px rgba(0,0,0,0.55)', flexShrink: 0, background: bgImage ? 'transparent' : 'var(--bg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: desktop ? 12 : 6, padding: desktop ? '10px 32px 12px' : '8px 12px 10px', ...(!desktop && mainTab === 'floor' ? { boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)' } : {}) }}>
          {/* Tab buttons — fixed width, don't grow */}
          {['floor', 'recent', 'lot'].map((t, i) => (<>
          {i > 0 && <div style={{ width: 1, height: 24, background: bgImage ? 'rgba(255,255,255,0.28)' : 'var(--border)', boxShadow: '-10px 0 14px rgba(0,0,0,0.55), 10px 0 14px rgba(0,0,0,0.55)', flexShrink: 0 }} />}
            <button key={t} onClick={() => setMainTab(t)} className="text-3d" style={{
              padding: desktop ? '8px 18px' : '7px 10px',
              flexShrink: 0,
              borderRadius: desktop ? 8 : 10,
              border: 'none',
              fontSize: '1rem', fontWeight: 700,
              fontFamily: 'var(--font-display)', letterSpacing: '1px', textTransform: 'uppercase',
              background: 'transparent',
              color: mainTab === t ? 'var(--orange)' : 'var(--text)',
              cursor: 'pointer', transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}>
              {t === 'floor' ? 'Floor' : t === 'recent' ? 'Recent' : 'Lot'}
            </button>
          </>))}

          {/* Search bar — desktop: inline here, stretches to fill remaining space.
              Mobile: too many things fight for this row's width (3 tabs + 2 icon
              buttons), squeezing the input's placeholder down to 1-2 characters —
              it gets its own full-width row below instead, see after this row. */}
          {desktop && mainTab === 'floor' && (
            <div className="float-3d" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)', border: 'none', borderRadius: 8, paddingLeft: 10, paddingRight: 12, height: 42 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Plate, client, adjuster, insurer, claim #..."
                style={{ background: 'none', border: 'none', padding: '11px 0', fontSize: 15, color: 'var(--text)', width: '100%' }} />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer', padding: '0 2px' }}>×</button>}
            </div>
          )}
          {desktop && mainTab !== 'floor' && <div style={{ flex: 1 }} />}
          {!desktop && <div style={{ flex: 1 }} />}

          {/* Edit Lot (mobile only, Lot tab only) — sits in the exact slot the Tools
              button normally occupies, since Tools has no reason to be reachable
              while you're looking at the Lot and screen space here is tight */}
          {!desktop && mainTab === 'lot' && room.role === 'admin' && (
            <button onClick={() => setLotEditMode(v => !v)} className={lotEditMode ? 'raise-3d-orange' : 'raise-3d'} style={{ display: 'flex', alignItems: 'center', gap: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '8px 13px', borderRadius: 9, background: lotEditMode ? 'var(--orange)' : 'var(--bg2)', color: lotEditMode ? '#16100b' : 'var(--text2)', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
              {lotEditMode ? 'Done' : 'Edit Lot'}
            </button>
          )}

          {/* Tools dropdown (mobile only, hidden on the Lot tab — see above) */}
          {!desktop && mainTab !== 'lot' && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button onClick={() => setShowToolsMenu(v => !v)} className="raise-3d" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: showToolsMenu ? 'var(--bg4)' : (bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)'), border: showToolsMenu ? '1px solid var(--orange)' : 'none', borderRadius: 10, cursor: 'pointer', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
                  <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
                  <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
                </svg>
              </button>
              {showToolsMenu && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setShowToolsMenu(false)} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 200, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, minWidth: 220, maxHeight: 'min(60vh, 480px)', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch', transform: 'translateZ(0)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                    {[
                      { icon: <IcoInsurance size={18} />, label: 'Insurance Contacts', action: () => { setShowInsurance(true); setShowToolsMenu(false); } },
                      { icon: <IcoContacts size={18} />, label: 'Important Contacts', action: () => { setShowContacts(true); setShowToolsMenu(false); } },
                      { icon: <IcoCompare size={18} />, label: 'Compare Estimates', action: () => { setShowCompare(true); setShowToolsMenu(false); } },
                      { icon: <IcoInvoice size={18} />, label: 'New Invoice', action: () => { setShowInvoice(true); setShowToolsMenu(false); } },
                      { icon: <IcoTasks size={18} />, label: 'Shop Tasks', action: () => { setShowTodos(true); setShowToolsMenu(false); } },
                      { icon: <IcoCalendar size={18} />, label: 'Calendar', action: () => { setShowCalendar(true); setShowToolsMenu(false); } },
                      { icon: <IcoMail size={18} />, label: 'Emails', badge: emailsUnread, action: () => { openEmails(); setShowToolsMenu(false); } },
                      { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>, label: unreviewedCount > 0 ? `New Requests (${unreviewedCount})` : 'New Requests', badge: unreviewedCount > 0, action: () => { setShowRequests(true); setShowToolsMenu(false); } },
                      ...(room.role === 'admin' ? [{ icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="20" x2="5" y2="12"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="19" y1="20" x2="19" y2="9"/></svg>, label: 'Shop Stats', action: () => { setShowStats(true); setShowToolsMenu(false); } }] : []),
                    ].map((item, i, arr) => (
                      <button key={item.label} onClick={item.action} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'none', border: 'none', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 14, fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--text)', textAlign: 'left' }}>
                        <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, color: 'var(--text2)', flexShrink: 0 }}>
                          {item.icon}
                          {item.badge && <span className="unread-dot" style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: 'var(--orange)' }} />}
                        </span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Grid / list view toggle */}
          {mainTab === 'floor' && (
            <button onClick={toggleViewMode} className="raise-3d" title={viewMode === 'grid' ? 'Compact list' : 'Card grid'} style={{ width: desktop ? 42 : 40, height: desktop ? 42 : 40, padding: 0, lineHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)', border: 'none', borderRadius: desktop ? 8 : 10, cursor: 'pointer', flexShrink: 0, color: 'var(--text2)' }}>
              {viewMode === 'grid'
                ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ display: 'block' }}><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>}
            </button>
          )}

          {/* + Claim button — desktop only; mobile uses the floating button */}
          {room.role === 'admin' && desktop && (
            <button className="btn btn-primary text-inset" onClick={() => setShowNewClaim(true)}
              style={{ padding: '0 12px', height: 42, fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              + Claim
            </button>
          )}
        </div>

        {/* Search bar — mobile only, its own full-width row (see comment above) */}
        {!desktop && mainTab === 'floor' && (
          <div style={{ padding: '8px 12px' }}>
            <div className="float-3d" style={{ display: 'flex', alignItems: 'center', gap: 10, background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)', border: 'none', borderRadius: 10, paddingLeft: 10, paddingRight: 12, height: 40 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
                style={{ background: 'none', border: 'none', padding: '10px 0', fontSize: 14, color: 'var(--text)', width: '100%' }} />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer', padding: '0 2px' }}>×</button>}
            </div>
          </div>
        )}
      </div>

      {/* ── MAIN CONTENT ── */}
      {mainTab === 'lot' ? (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          <ParkingLot session={session} room={room} claims={claims} onOpenClaim={openClaim} desktop={desktop} bgImage={bgImage} editMode={lotEditMode} setEditMode={setLotEditMode} />
        </div>
      ) : (
      <div style={{ flex: 1, overflowY: 'auto', padding: desktop ? '20px 32px' : '14px 16px', paddingBottom: desktop ? 20 : 'calc(14px + env(safe-area-inset-bottom))' }}>
        {mainTab === 'floor' ? (
          loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: desktop ? 'repeat(auto-fill, minmax(360px, 1fr))' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: desktop ? 16 : 12 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton-card" style={{ height: desktop ? 190 : 150, borderRadius: 15, animationDelay: `${i * 0.08}s` }} />
              ))}
            </div>
          )
          : loadError && claims.length === 0 ? (
            <div className="empty-state">
              <div className="icon">⚠️</div>
              <div className="title">Couldn't load claims</div>
              <div className="sub">Check your connection</div>
              <button className="btn btn-primary" onClick={() => { setLoading(true); loadClaims(); }} style={{ marginTop: 12, padding: '10px 24px' }}>Retry</button>
            </div>
          )
          : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🔧</div>
              <div className="title">{search || filterStage ? 'No matching claims' : 'No claims on the floor'}</div>
              <div className="sub">{!search && !filterStage && room.role === 'admin' ? 'Click + Claim to write one up' : ''}</div>
            </div>
          ) : (
            viewMode === 'list' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                {filtered.map(claim => (
                  <ClaimRow key={claim.id} claim={claim} desktop={desktop} unread={isUnread(claim)} roomLabel={mergeRooms && claim.room_id !== room.id ? (roomsById[claim.room_id]?.shop_name || roomsById[claim.room_id]?.name || 'Other room') : null} onClick={() => openClaim(claim.id)} />
                ))}
              </div>
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: desktop ? 'repeat(auto-fill, minmax(360px, 1fr))' : 'repeat(auto-fill, minmax(300px, 1fr))', gridAutoRows: desktop ? '1fr' : 'auto', gap: desktop ? 16 : 12 }}>
              {filtered.map((claim, idx) => (
                <ClaimCard key={claim.id} claim={claim} idx={idx} desktop={desktop} warnings={getClaimWarnings(claim, stageSince[claim.id])} unread={isUnread(claim)} roomLabel={mergeRooms && claim.room_id !== room.id ? (roomsById[claim.room_id]?.shop_name || roomsById[claim.room_id]?.name || 'Other room') : null} onClick={() => openClaim(claim.id)} />
              ))}
            </div>
            )
          )
        ) : (
          // ── RECENT TAB — expandable rows ──
          loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: desktop ? 780 : 'none' }}>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="skeleton-card" style={{ height: 74, borderRadius: 12, animationDelay: `${i * 0.06}s` }} />
              ))}
            </div>
          )
          : recentClaims.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🕐</div>
              <div className="title">No claims yet</div>
              <div className="sub">Recently updated claims show up here</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: desktop ? 780 : 'none' }}>
              {(() => {
                const today = new Date(); today.setHours(0,0,0,0);
                const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
                function dateLabel(d) {
                  const day = new Date(d); day.setHours(0,0,0,0);
                  if (day.getTime() === today.getTime()) return 'Today';
                  if (day.getTime() === yesterday.getTime()) return 'Yesterday';
                  return day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                }
                const groups = [];
                let lastLabel = null;
                recentClaims.forEach(claim => {
                  const label = dateLabel(claim.updated_at);
                  if (label !== lastLabel) { groups.push({ label, claims: [] }); lastLabel = label; }
                  groups[groups.length - 1].claims.push(claim);
                });
                return groups.map(group => (
                  <div key={group.label} style={{ marginBottom: desktop ? 20 : 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text3)', padding: desktop ? '0 2px 8px' : '0 2px 6px' }}>{group.label}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: desktop ? 8 : 6 }}>
                      {group.claims.map((claim, idx) => {
                const isExpanded = expandedRecentId === claim.id;
                const updates = recentUpdates[claim.id];
                const color = STAGE_COLORS[claim.status] || 'var(--text3)';
                const progress = stageProgress(claim.status);
                return (
                  <div key={claim.id} style={{ background: 'var(--bg2)', border: `1px solid ${isExpanded ? 'var(--orange)' : 'var(--border)'}`, borderRadius: 13, overflow: 'hidden', transition: 'border-color 0.15s', animationDelay: `${idx * 0.03}s` }} className="claim-card">

                    {/* Row header — tap to expand */}
                    <div onClick={() => toggleRecentExpand(claim.id)} style={{ padding: desktop ? '14px 18px' : '12px 14px', display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 20 : 17, fontWeight: 700 }}>{claim.car || 'Unknown'}</span>
                          {claim.plate && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>{claim.plate}</span>}
                        </div>
                        <div style={{ fontSize: desktop ? 13 : 12, color: 'var(--text3)', marginTop: 3 }}>
                          {claim.client_name && <span>{claim.client_name} · </span>}
                          {claim.insurer && <span>{claim.insurer} · </span>}
                          <span>Updated {timeAgo(claim.updated_at)}</span>
                        </div>
                        <div style={{ height: 2, background: 'var(--bg4)', borderRadius: 1, marginTop: 7, overflow: 'hidden', maxWidth: 180 }}>
                          <div style={{ height: '100%', width: `${progress}%`, background: color, borderRadius: 1 }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 20, whiteSpace: 'nowrap', background: `${color}22`, color, border: `1px solid ${color}44` }}>{claim.status}</div>
                        <div style={{ color: 'var(--text3)', fontSize: 14, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▾</div>
                      </div>
                    </div>

                    {/* Expanded: recent updates + open button */}
                    {isExpanded && (
                      <div className="recent-updates" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)', padding: desktop ? '14px 18px' : '12px 14px' }}>
                        {!updates ? (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><div className="spinner" /></div>
                        ) : updates.length === 0 ? (
                          <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>No updates yet</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                            {updates.map(u => {
                              const isLog = u._type === 'log';
                              const accentColor = isLog
                                ? (u.action === 'part_arrived' ? 'var(--green)' : u.action === 'tag_added' ? 'var(--blue)' : 'var(--text3)')
                                : 'var(--orange)';
                              const label = isLog ? u.detail : u.text;
                              return (
                                <div key={u.id} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', borderLeft: `3px solid ${accentColor}` }}>
                                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text)' }}>{label}</div>
                                  <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>{u.user_name} · {timeAgo(u.created_at)}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <button
                          onClick={() => openClaim(claim.id)}
                          className="btn btn-primary"
                          style={{ width: '100%', padding: '10px', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}
                        >
                          Open Full Claim →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )
        )}
      </div>
      )}

      </div>{/* end content wrapper */}

      {/* ── CONTACTS POPUP ── */}
      {showContacts && (
        <ImportantContacts room={room} session={session} onClose={() => setShowContacts(false)} desktop={desktop} />
      )}

      {/* ── INSURANCE POPUP ── */}
      {showInsurance && (
        <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}
          onClick={e => e.target === e.currentTarget && setShowInsurance(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.7)' }} onClick={() => { setShowInsurance(false); setInsuranceSearch(''); }} />
          <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 700, maxHeight: desktop ? '80vh' : undefined }}>
            {/* Header */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72, padding: '0 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>Insurers</div>
              <button onClick={() => { setShowInsurance(false); setInsuranceSearch(''); }} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
            </div>
            {/* Search */}
            <div style={{ padding: '10px 16px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
              <InsuranceSearch onSearch={q => setInsuranceSearch(q)} />
            </div>
            {/* List */}
            <div className="modal-scroll" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {INSURERS.filter(ins => ins.name.toLowerCase().includes(insuranceSearch.toLowerCase())).map(ins => {
                const domain = ins.web.split('/')[0];
                return (
                  <div key={ins.name} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 }}>
                      <img
                        src={`https://logo.uplead.com/${domain}`}
                        alt={ins.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                      <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#555' }}>
                        {ins.name[0]}
                      </div>
                    </div>
                    <div style={{ flex: 1, fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{ins.name}</div>
                    <a href={`tel:${ins.phone}`} style={{ color: 'var(--green)', fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
                      📞 {ins.phone}
                    </a>
                    <a href={`https://${ins.web}`} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', fontSize: 12, textDecoration: 'none', flexShrink: 0 }}>
                      🌐 {desktop ? ins.web : 'Site'}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── CALENDAR POPUP ── */}
      {showCalendar && (
        <CalendarTab room={room} session={session} onClose={() => setShowCalendar(false)} desktop={desktop} />
      )}

      {/* ── SHOP STATS POPUP (admin) ── */}
      {showStats && (
        <ShopStats claims={claims} room={room} onClose={() => setShowStats(false)} desktop={desktop} />
      )}

      {/* ── EMAILS POPUP ── */}
      {showEmails && (
        <EmailsWindow room={room} claims={claims} onClose={() => { setShowEmails(false); loadEmailsLatest(); }} onOpenClaim={id => { setShowEmails(false); openClaim(id); }} desktop={desktop} />
      )}

      {/* ── TODOS POPUP ── */}
      {showTodos && (
        <TodoPanel
          room={room}
          session={session}
          claims={claims}
          onClose={() => setShowTodos(false)}
          onOpenClaim={id => { setShowTodos(false); openClaim(id); }}
          desktop={desktop}
        />
      )}

      {/* ── COMPARE POPUP ── */}
      {showCompare && (
        <div className="modal-overlay" style={{ zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.7)' }} onClick={() => setShowCompare(false)} />
          <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg)', border: '1px solid var(--border)', maxWidth: 780, maxHeight: desktop ? '88vh' : undefined }}>
            <ComparePage desktop={desktop} onBack={() => setShowCompare(false)} />
          </div>
        </div>
      )}

      {/* ── NEW REQUESTS POPUP ── */}
      {showRequests && (
        <div className="modal-overlay" style={{ zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.7)' }} onClick={() => { setShowRequests(false); loadClaims(); }} />
          <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg)', border: '1px solid var(--border)', maxWidth: 640, maxHeight: desktop ? '88vh' : undefined }}>
            <NewRequestsPage desktop={desktop} room={room} session={session}
              onOpenClaim={(id) => { setShowRequests(false); setSelectedClaimId(id); }}
              onBack={() => { setShowRequests(false); loadClaims(); }} />
          </div>
        </div>
      )}

      {/* ── INVOICE POPUP ── */}
      {showInvoice && (
        <div className="modal-overlay" style={{ zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.7)' }} onClick={() => setShowInvoice(false)} />
          <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 680, maxHeight: desktop ? '88vh' : undefined }}>
            <div style={{ boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', padding: '0 20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', height: 72 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>New Invoice</div>
              <button onClick={() => setShowInvoice(false)} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
            </div>
            <div className="modal-scroll" style={{ flex: 1 }}>
              <InvoiceTab claim={{}} room={room} />
            </div>
          </div>
        </div>
      )}

      {/* ── FLOATING + CLAIM (mobile) ── */}
      {!desktop && mainTab !== 'lot' && room.role === 'admin' && !selectedClaimId && !showNewClaim && !showToolsMenu && (
        <button onClick={() => setShowNewClaim(true)} aria-label="New claim" className="fab-3d" style={{
          position: 'fixed', right: 16, bottom: 'calc(20px + env(safe-area-inset-bottom))',
          width: 58, height: 58, borderRadius: '50%',
          color: '#16100b', border: 'none', padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 90, cursor: 'pointer',
        }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
            {/* letterpress: dark edge above, light catch below, face color on top */}
            <g stroke="rgba(0,0,0,0.45)" strokeWidth="2.6" strokeLinecap="round" transform="translate(0 -0.8)"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/></g>
            <g stroke="rgba(255,255,255,0.28)" strokeWidth="2.6" strokeLinecap="round" transform="translate(0 0.8)"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/></g>
            <g stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/></g>
          </svg>
        </button>
      )}

      {/* ── MODALS ── */}
      {selectedClaimId && (
        <ClaimModal claimId={selectedClaimId} session={session} room={selectedClaimRoom || room}
          roomName={(selectedClaimRoom || room).shop_name || (selectedClaimRoom || room).name}
          onClose={() => { setSelectedClaimId(null); setSelectedClaimRoom(null); loadClaims(); }}
          onDeleted={(id) => { setSelectedClaimId(null); setSelectedClaimRoom(null); setClaims(prev => prev.filter(c => c.id !== id)); }}
          initialClaim={room.id === DEV_ROOM_ID ? DEV_CLAIMS.find(c => c.id === selectedClaimId) : undefined} />
      )}
      {showNewClaim && (
        <NewClaimModal session={session} room={room} onClose={() => setShowNewClaim(false)}
          onCreated={(claim) => { setShowNewClaim(false); loadClaims(); openClaim(claim.id); }} />
      )}

      {/* ── SLIDE FROM RIGHT MENU ── */}
      {menuOpen && (
        <div className="modal-overlay" style={{ zIndex: 200, display: 'flex', justifyContent: 'flex-end', animation: 'none' }}
          onClick={e => e.target === e.currentTarget && closeMenu()}>
          {/* Backdrop */}
          <div className={`menu-fade${menuClosing ? ' closing' : ''}`} style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.62)' }} onClick={closeMenu} />

          {/* Panel — slides from right */}
          <div className={`menu-slide${menuClosing ? ' closing' : ''}`} style={{
            position: 'relative', zIndex: 1,
            width: desktop ? 380 : '85vw', maxWidth: 420,
            height: '100%',
            background: 'var(--bg2)', borderLeft: '1px solid var(--border)',
            boxShadow: '-16px 0 40px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            <RoomMenu
              screen={menuScreen}
              setScreen={setMenuScreen}
              session={session}
              room={room}
              setRoom={setRoom}
              onClose={closeMenu}
              onSwitchRoom={() => { closeMenu(); onSwitchRoom(); }}
              onLeaveRoom={async () => {
                await supabase.from('room_members').delete().eq('room_id', room.id).eq('user_id', session.user.id);
                localStorage.removeItem('shoptrack_room_id');
                closeMenu();
                onSwitchRoom();
              }}
              desktop={desktop}
              bgImage={bgImage}
              setBgImage={(val) => {
                setBgImage(val);
                if (val) localStorage.setItem(`shoptrack_bg_${room.id}`, val);
                else localStorage.removeItem(`shoptrack_bg_${room.id}`);
              }}
              bgBlur={bgBlur}
              setBgBlur={(val) => {
                setBgBlur(val);
                localStorage.setItem(`shoptrack_bgblur_${room.id}`, String(val));
              }}
              mergeRooms={mergeRooms}
              setMergeRooms={(val) => {
                setMergeRooms(val);
                if (val) localStorage.setItem('shoptrack_merge', '1');
                else localStorage.removeItem('shoptrack_merge');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── ROOM MENU ─────────────────────────────────────────────────────────────────
function RoomMenu({ screen, setScreen, session, room, setRoom, onClose, onSwitchRoom, onLeaveRoom, desktop, bgImage, setBgImage, bgBlur, setBgBlur, mergeRooms, setMergeRooms }) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [allRooms, setAllRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [roomName, setRoomName] = useState(room.shop_name || room.name || '');
  const [savingName, setSavingName] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connectingEmail, setConnectingEmail] = useState(false);
  const [disconnectingEmail, setDisconnectingEmail] = useState(false);
  const [newCheckTime, setNewCheckTime] = useState('08:00');
  const [savingTime, setSavingTime] = useState(false);
  // Shop info (feeds invoices) + chosen invoice template — stored on the room
  const [shopAddress, setShopAddress] = useState(room.shop_address || '');
  const [shopPhone, setShopPhone] = useState(room.shop_phone || '');
  const [shopEmail, setShopEmail] = useState(room.shop_email || '');
  const [savingShopInfo, setSavingShopInfo] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    loadAllRooms();
  }, []);

  useEffect(() => {
    if (screen === 'members') loadMembers();
  }, [screen]);

  // A tab opened for "Connect Gmail" finishes on Google's own page, not back
  // in the app — so when the user switches back to this tab, quietly check
  // whether the connection went through and update the UI if so.
  useEffect(() => {
    if (screen !== 'shop' || room.role !== 'admin') return;
    async function refreshConnectedEmail() {
      const { data } = await supabase.from('rooms').select('connected_email').eq('id', room.id).single();
      if (data && data.connected_email !== room.connected_email) {
        setRoom(r => ({ ...r, connected_email: data.connected_email }));
      }
    }
    window.addEventListener('focus', refreshConnectedEmail);
    return () => window.removeEventListener('focus', refreshConnectedEmail);
  }, [screen, room.role, room.id, room.connected_email, setRoom]);

  // "HH:MM" typed in the shop's local clock <-> "HH:MM" stored/matched in UTC
  function localToUtcHHMM(local) {
    const [h, m] = local.split(':').map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0);
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  }
  function utcToLocalHHMM(utc) {
    const [h, m] = utc.split(':').map(Number);
    const d = new Date(); d.setUTCHours(h, m, 0, 0);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  async function connectGmail() {
    setConnectingEmail(true);
    const { data, error } = await supabase.functions.invoke('gmail-oauth', { body: { action: 'start', room_id: room.id } });
    setConnectingEmail(false);
    if (error || !data?.url) { toast('Could not start the Gmail connection — try again.', 'error'); return; }
    window.open(data.url, '_blank', 'noopener');
  }

  async function disconnectGmail() {
    if (!window.confirm('Disconnect this Gmail account? Existing email threads stay on their claims.')) return;
    setDisconnectingEmail(true);
    const prev = room.connected_email;
    setRoom(r => ({ ...r, connected_email: null }));
    const { error } = await supabase.functions.invoke('gmail-oauth', { body: { action: 'disconnect', room_id: room.id } });
    setDisconnectingEmail(false);
    if (error) { setRoom(r => ({ ...r, connected_email: prev })); toast('Could not disconnect — try again.', 'error'); }
  }

  async function regenerateIntakeLink() {
    if (!window.confirm('Regenerate the intake link? The old link will stop working immediately.')) return;
    const newToken = Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, '0')).join('');
    const { error } = await supabase.from('rooms').update({ intake_token: newToken }).eq('id', room.id);
    if (error) { toast('Could not regenerate link', 'error'); return; }
    setRoom({ ...room, intake_token: newToken });
    toast('New intake link created', 'success');
  }

  async function saveShopInfo() {
    setSavingShopInfo(true);
    const patch = { shop_address: shopAddress.trim() || null, shop_phone: shopPhone.trim() || null, shop_email: shopEmail.trim() || null };
    const { error } = await supabase.from('rooms').update(patch).eq('id', room.id);
    setSavingShopInfo(false);
    if (error) { toast('Could not save shop info', 'error'); return; }
    setRoom(r => ({ ...r, ...patch }));
    toast('Shop info saved', 'success');
  }

  async function selectTemplate(key) {
    if (key === room.invoice_template) return;
    setSavingTemplate(true);
    const { error } = await supabase.from('rooms').update({ invoice_template: key }).eq('id', room.id);
    setSavingTemplate(false);
    if (error) { toast('Could not save invoice style', 'error'); return; }
    setRoom(r => ({ ...r, invoice_template: key }));
    toast('Invoice style updated', 'success');
  }

  async function addCheckTime() {
    if (!newCheckTime) return;
    const utc = localToUtcHHMM(newCheckTime);
    const current = room.email_check_times || [];
    if (current.includes(utc)) return;
    const next = [...current, utc].sort();
    setSavingTime(true);
    setRoom(r => ({ ...r, email_check_times: next }));
    const { error } = await supabase.from('rooms').update({ email_check_times: next }).eq('id', room.id);
    setSavingTime(false);
    if (error) { setRoom(r => ({ ...r, email_check_times: current })); toast('Could not save that time — try again.', 'error'); }
  }

  async function removeCheckTime(utc) {
    const current = room.email_check_times || [];
    const next = current.filter(t => t !== utc);
    setRoom(r => ({ ...r, email_check_times: next }));
    const { error } = await supabase.from('rooms').update({ email_check_times: next }).eq('id', room.id);
    if (error) { setRoom(r => ({ ...r, email_check_times: current })); toast('Could not remove that time — try again.', 'error'); }
  }

  async function loadMembers() {
    if (room.id === DEV_ROOM_ID) { setMembers(DEV_MEMBERS); setLoadingMembers(false); return; }
    setLoadingMembers(true);
    const { data } = await supabase.from('room_members').select('*').eq('room_id', room.id).order('joined_at', { ascending: true });
    if (data) setMembers(data);
    setLoadingMembers(false);
  }

  async function loadAllRooms() {
    setLoadingRooms(true);
    const { data } = await supabase.from('room_members').select('*, rooms(*)').eq('user_id', session.user.id).order('joined_at', { ascending: false });
    if (data) setAllRooms(data.filter(m => m.rooms));
    setLoadingRooms(false);
  }

  async function removeMember(id) {
    if (!window.confirm('Remove this member?')) return;
    const removed = members.find(m => m.id === id);
    // Optimistic: remove from the list immediately, restore if the delete fails
    setMembers(prev => prev.filter(m => m.id !== id));
    if (room.id === DEV_ROOM_ID) return; // dev room isn't a real row — nothing to persist
    const { error } = await supabase.from('room_members').delete().eq('id', id);
    if (error && removed) {
      setMembers(prev => [...prev, removed]);
      toast('Could not remove the member — check your connection and try again.', 'error');
    }
  }

  async function changeMemberRole(id, newRole) {
    const member = members.find(m => m.id === id);
    if (!member) return;
    const verb = newRole === 'admin' ? 'an admin' : 'a member';
    if (!window.confirm(`Make ${member.user_name || 'this person'} ${verb}?`)) return;
    const prevRole = member.role;
    // Optimistic: flip the role immediately, restore if the update fails
    setMembers(prev => prev.map(m => (m.id === id ? { ...m, role: newRole } : m)));
    if (room.id === DEV_ROOM_ID) return; // dev room isn't a real row — nothing to persist
    const { error } = await supabase.from('room_members').update({ role: newRole }).eq('id', id);
    if (error) {
      setMembers(prev => prev.map(m => (m.id === id ? { ...m, role: prevRole } : m)));
      toast('Could not change their role — check your connection and try again.', 'error');
    }
  }

  async function switchToRoom(roomData, role) {
    localStorage.setItem('shoptrack_room_id', roomData.id);
    setRoom({ ...roomData, role });
    onClose();
  }

  async function saveRoomName() {
    if (!roomName.trim()) return;
    setSavingName(true);
    await supabase.from('rooms').update({ shop_name: roomName.trim() }).eq('id', room.id);
    setRoom(r => ({ ...r, shop_name: roomName.trim() }));
    setSavingName(false);
    setEditingName(false);
  }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    const ext = file.name.split('.').pop();
    const path = `${room.id}/logos/logo.${ext}`;
    const { error } = await supabase.storage.from('claim-files').upload(path, file, { upsert: true });
    if (error) { toast('Upload failed: ' + error.message); setUploadingLogo(false, 'error'); return; }
    const { data: urlData } = await supabase.storage.from('claim-files').createSignedUrl(path, 60 * 60 * 24 * 365);
    if (urlData?.signedUrl) {
      await supabase.from('rooms').update({ logo_url: urlData.signedUrl }).eq('id', room.id);
      setRoom(r => ({ ...r, logo_url: urlData.signedUrl }));
    }
    setUploadingLogo(false);
    e.target.value = '';
  }

  async function removeLogo() {
    if (!window.confirm('Remove shop logo?')) return;
    await supabase.from('rooms').update({ logo_url: null }).eq('id', room.id);
    setRoom(r => ({ ...r, logo_url: null }));
  }

  async function signOut() {
    localStorage.removeItem('shoptrack_room_id');
    await supabase.auth.signOut();
  }

  function copyCode() {
    navigator.clipboard.writeText(room.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const userName = session.user.user_metadata?.full_name || session.user.email;
  const avatarUrl = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null;
  const otherRooms = allRooms.filter(m => m.rooms.id !== room.id);

  // Direction-aware screen switch: forward slides in from the right, back from the left
  const dirRef = useRef('none');
  function go(to, dir = 'fwd') { dirRef.current = dir; setScreen(to); }
  const scrCls = dirRef.current === 'none' ? undefined : (dirRef.current === 'back' ? 'menu-screen-back' : 'menu-screen-fwd');

  function Header({ title, back }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
        {back && (
          <button onClick={() => go(back, 'back')} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: 8, padding: '6px 10px 6px 6px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
        )}
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 700, flex: 1, letterSpacing: '0.4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        <button onClick={onClose} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
      </div>
    );
  }

  const rolePill = (
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 9px', borderRadius: 20, flexShrink: 0, background: room.role === 'admin' ? 'rgba(249,115,22,0.15)' : 'var(--bg4)', color: room.role === 'admin' ? 'var(--orange)' : 'var(--text2)', border: `1px solid ${room.role === 'admin' ? 'rgba(249,115,22,0.4)' : 'var(--border)'}` }}>{room.role === 'admin' ? 'Admin' : 'Member'}</span>
  );

  // ── MAIN — category list only; every feature lives on its own screen ──
  if (screen === 'main') {
    const currentTemplate = (TEMPLATE_KEYS.find(t => t.key === (room.invoice_template || 'ignition')) || TEMPLATE_KEYS[0]).name;
    return (
      <div key="main" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 14px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--orange-dim)', border: '1px solid var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: 'var(--orange)', flexShrink: 0, overflow: 'hidden' }}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : userName[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.user.email}</div>
          </div>
          <button onClick={onClose} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
        </div>

        <div className="modal-scroll" style={{ flex: 1, padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          <MenuSection>
            <MenuRow tint="#f97316"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></svg>}
              label="Shop" value={room.shop_name || room.name} onClick={() => go('shop')} />
            <MenuRow tint="#3b82f6"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
              label="Members" onClick={() => go('members')} />
            <MenuRow tint="#22c55e"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>}
              label="Rooms" value={allRooms.length > 1 ? `${allRooms.length} joined` : undefined} onClick={() => go('rooms')} />
            <MenuRow tint="#eab308"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>}
              label="Preferences" onClick={() => go('prefs')} />
          </MenuSection>

          <MenuSection>
            <MenuRow danger right={null}
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>}
              label="Sign Out" onClick={signOut} />
          </MenuSection>
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '12px 18px 14px', boxShadow: 'inset 0 9px 9px -9px rgba(0,0,0,0.6)', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1 }}>Shop<span style={{ color: 'var(--orange)' }}>Track</span></div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Version 6 · Body Shop Manager</div>
        </div>
      </div>
    );
  }

  // ── ROOMS — current room, switch, join, leave ──
  if (screen === 'rooms') {
    return (
      <div key="rooms" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Rooms" back="main" />
        <div className="modal-scroll" style={{ flex: 1, padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          <MenuSection label="Current Room">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', boxShadow: 'inset 3px 0 0 var(--orange)' }}>
              <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--orange-dim)', color: 'var(--orange)' }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.shop_name || room.name}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', marginTop: 2, letterSpacing: '0.1em' }}>{room.code}</div>
              </div>
              {rolePill}
            </div>
          </MenuSection>

          {otherRooms.length > 0 && (
            <MenuSection label="Switch To" foot="Tap a room to make it your active floor.">
              {otherRooms.map(m => (
                <div key={m.id} className="menu-row" onClick={() => switchToRoom(m.rooms, m.role)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg4)', color: 'var(--text3)' }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.rooms.shop_name || m.rooms.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', marginTop: 2, letterSpacing: '0.1em' }}>{m.rooms.code}</div>
                  </div>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                </div>
              ))}
            </MenuSection>
          )}

          <MenuSection>
            <MenuRow
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>}
              label="Join Another Room" onClick={onSwitchRoom} />
            {room.role !== 'admin' && (
              <MenuRow danger right={null}
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
                label="Leave Room" onClick={onLeaveRoom} />
            )}
          </MenuSection>
        </div>
      </div>
    );
  }

  // ── PREFERENCES (this device only) ──
  if (screen === 'prefs') {
    const sLabel = { fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: '0 4px 7px' };
    return (
      <div key="prefs" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Preferences" back="main" />
        <div className="modal-scroll" style={{ flex: 1, padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Background */}
          <div>
            <div style={sLabel}>Floor Background</div>
            {bgImage && (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 8, height: 80 }}>
                {bgImage.startsWith('#')
                  ? <div style={{ width: '100%', height: '100%', background: bgImage }} />
                  : <img src={bgImage} alt="Background" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: `blur(${bgBlur * 0.4}px)`, transform: 'scale(1.08)' }} />}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' }}>
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>Background set</span>
                  <button onClick={() => setBgImage(null)} className="btn btn-danger" style={{ fontSize: 12, padding: '4px 10px' }}>Remove</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="raise-3d" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', background: 'var(--bg3)', border: '1.5px dashed var(--border)', borderRadius: 12, cursor: 'pointer' }}>
                <span style={{ fontSize: 20 }}>🖼</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text2)' }}>{bgImage ? 'Replace' : 'Image'}</span>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => setBgImage(ev.target.result);
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }} />
              </label>
              <label className="raise-3d" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', background: 'var(--bg3)', border: '1.5px dashed var(--border)', borderRadius: 12, cursor: 'pointer' }}>
                <span style={{ fontSize: 20 }}>🎨</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text2)' }}>Color</span>
                <input type="color" value={bgImage && bgImage.startsWith('#') ? bgImage : '#0c0c0e'} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} onChange={e => setBgImage(e.target.value)} />
              </label>
            </div>
            {bgImage && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>Blur</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{bgBlur}px</span>
                </div>
                <input type="range" min="0" max="40" step="1" value={bgBlur} onChange={e => setBgBlur(Number(e.target.value))} style={{ width: '100%', padding: 0, border: 'none', background: 'transparent', accentColor: 'var(--orange)', cursor: 'pointer', height: 24 }} />
              </div>
            )}
          </div>

          <MenuSection label="Floor" foot="Shows claims from every room you're in on this floor.">
            <MenuRow tint="#06b6d4"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>}
              label="All rooms on one floor"
              right={
                <button
                  onClick={() => setMergeRooms(!mergeRooms)}
                  role="switch"
                  aria-checked={mergeRooms}
                  style={{ position: 'relative', width: 44, height: 26, borderRadius: 13, border: 'none', flexShrink: 0, cursor: 'pointer', background: mergeRooms ? 'var(--orange)' : 'var(--bg4)', transition: 'background 0.15s' }}>
                  <span style={{ position: 'absolute', top: 3, left: mergeRooms ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                </button>
              } />
          </MenuSection>

          <div style={{ fontSize: 11, color: 'var(--text3)', margin: '-8px 4px 0', lineHeight: 1.5 }}>Preferences are stored on this device only.</div>
        </div>
      </div>
    );
  }

  // ── SHOP — everything about the shop itself: name, logo, contact, invoicing, intake, email ──
  if (screen === 'shop') {
    const isAdmin = room.role === 'admin';
    const fieldStyle = { width: '100%', padding: '11px 13px', fontSize: 14, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', outline: 'none' };
    const sLabel = { fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: '0 4px 7px' };
    const currentTemplate = (TEMPLATE_KEYS.find(t => t.key === (room.invoice_template || 'ignition')) || TEMPLATE_KEYS[0]).name;
    return (
      <div key="shop" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Shop" back="main" />
        <div className="modal-scroll" style={{ flex: 1, padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Shop name */}
          <div>
            <div style={sLabel}>Shop Name</div>
            {editingName ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={roomName} onChange={e => setRoomName(e.target.value)} style={{ flex: 1 }} autoFocus />
                <button onClick={saveRoomName} disabled={savingName} className="btn btn-primary" style={{ padding: '0 14px', fontSize: 13 }}>
                  {savingName ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
                </button>
                <button onClick={() => { setEditingName(false); setRoomName(room.shop_name || room.name || ''); }} className="btn btn-secondary" style={{ padding: '0 10px', fontSize: 13 }}>✕</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{room.shop_name || room.name}</div>
                {isAdmin && (
                  <button onClick={() => setEditingName(true)} style={{ background: 'none', border: 'none', color: 'var(--orange)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Edit</button>
                )}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text3)', margin: '6px 4px 0' }}>Shown in the app header and printed on invoices.</div>
          </div>

          {/* Logo */}
          <div>
            <div style={sLabel}>Logo</div>
            {room.logo_url ? (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <img src={room.logo_url} alt="Shop logo" style={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
                <div style={{ flex: 1, fontSize: 12, color: 'var(--text3)' }}>Shown in header for all members</div>
                {isAdmin && <button className="btn btn-danger" onClick={removeLogo} style={{ fontSize: 12, padding: '5px 10px', flexShrink: 0 }}>Remove</button>}
              </div>
            ) : null}
            {isAdmin && (
              <label className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', background: uploadingLogo ? 'var(--bg3)' : 'var(--orange-dim)', border: `1.5px dashed ${uploadingLogo ? 'var(--border)' : 'var(--orange)'}`, borderRadius: 12, cursor: uploadingLogo ? 'default' : 'pointer' }}>
                {uploadingLogo
                  ? <><div className="spinner" style={{ width: 18, height: 18 }} /><span style={{ fontSize: 13, color: 'var(--text3)' }}>Uploading...</span></>
                  : <><span style={{ fontSize: 22 }}>🖼️</span><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)' }}>{room.logo_url ? 'Replace Logo' : 'Upload Logo'}</span><span style={{ fontSize: 12, color: 'var(--text3)' }}>PNG, JPG or SVG</span></>
                }
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} disabled={uploadingLogo} style={{ display: 'none' }} />
              </label>
            )}
          </div>

          {/* Contact info */}
          <div>
            <div style={sLabel}>Contact Info</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div className="label">Address</div>
                <textarea style={{ ...fieldStyle, resize: 'none', lineHeight: 1.5 }} rows={2} value={shopAddress} onChange={e => setShopAddress(e.target.value)} placeholder="1420 Industrial Pkwy, Dallas, TX 75201" disabled={!isAdmin} />
              </div>
              <div>
                <div className="label">Phone</div>
                <input style={fieldStyle} type="tel" value={shopPhone} onChange={e => setShopPhone(e.target.value)} placeholder="(214) 555-0148" disabled={!isAdmin} />
              </div>
              <div>
                <div className="label">Email</div>
                <input style={fieldStyle} type="email" value={shopEmail} onChange={e => setShopEmail(e.target.value)} placeholder="shop@email.com" disabled={!isAdmin} />
              </div>
              {isAdmin && (
                <button className="btn btn-primary" onClick={saveShopInfo} disabled={savingShopInfo} style={{ width: '100%' }}>
                  {savingShopInfo ? <div className="spinner" /> : 'Save Contact Info'}
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', margin: '7px 4px 0', lineHeight: 1.5 }}>Address and phone auto-fill the From section of every new invoice.</div>
          </div>

          {/* Invoicing & clients */}
          <MenuSection label="Invoicing & Clients">
            <MenuRow tint="#8b5cf6"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h5"/></svg>}
              label="Invoice Style" value={currentTemplate} onClick={() => go('invoicestyle')} />
            <MenuRow tint="#06b6d4"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>}
              label="Client Intake Link" onClick={() => go('intake')} />
          </MenuSection>

          {/* Email integration (admin only) */}
          {isAdmin && (
            <div>
              <div style={sLabel}>Email Integration</div>
              {room.connected_email ? (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.connected_email}</div>
                    <button onClick={disconnectGmail} disabled={disconnectingEmail} className="btn btn-danger" style={{ fontSize: 12, padding: '5px 10px', flexShrink: 0 }}>
                      {disconnectingEmail ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Disconnect'}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={connectGmail} disabled={connectingEmail} className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '13px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
                  {connectingEmail ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <>📧 Connect Gmail</>}
                </button>
              )}

              {room.connected_email && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Automatic check times</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {(room.email_check_times || []).length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>No scheduled times yet — add one below, or use Check Now in the Emails window.</div>
                    )}
                    {(room.email_check_times || []).map(utc => (
                      <div key={utc} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 20, padding: '5px 6px 5px 12px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                        {utcToLocalHHMM(utc)}
                        <button onClick={() => removeCheckTime(utc)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 15, padding: '0 4px', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="time" value={newCheckTime} onChange={e => setNewCheckTime(e.target.value)} style={{ flex: 1 }} />
                    <button onClick={addCheckTime} disabled={savingTime} className="btn btn-primary" style={{ padding: '0 16px', fontSize: 13 }}>
                      {savingTime ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '+ Add'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── INVOICE STYLE PICKER ──
  if (screen === 'invoicestyle') {
    const isAdmin = room.role === 'admin';
    const current = room.invoice_template || 'ignition';
    const shopName = room.shop_name || room.name;
    const CARD_W = 150; // thumbnail width; iframe renders the 760px doc scaled to fit
    return (
      <div key="invoicestyle" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Invoice Style" back="shop" />
        <div className="modal-scroll" style={{ flex: 1, padding: 18 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 14 }}>
            The look of every invoice your shop prints. {isAdmin ? 'Tap a style to set it.' : 'Only an admin can change it.'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(2, ${CARD_W}px)`, justifyContent: 'center', gap: 16 }}>
            {TEMPLATE_KEYS.map(t => {
              const active = t.key === current;
              return (
                <div key={t.key} onClick={() => { if (isAdmin && !savingTemplate) selectTemplate(t.key); }}
                  style={{ cursor: isAdmin ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <div style={{ position: 'relative', width: CARD_W, height: 195, borderRadius: 10, overflow: 'hidden', background: '#fff', border: `2px solid ${active ? 'var(--orange)' : 'var(--border)'}`, boxShadow: active ? '0 0 0 3px var(--orange-dim)' : 'none' }}>
                    <iframe
                      title={t.name}
                      srcDoc={invoicePreviewHtml(t.key, shopName)}
                      sandbox=""
                      scrolling="no"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{ border: 'none', width: 760, height: 990, transform: `scale(${CARD_W / 760})`, transformOrigin: 'top left', pointerEvents: 'none' }}
                    />
                    {active && (
                      <div style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16100b" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', color: active ? 'var(--orange)' : 'var(--text)' }}>{t.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── CLIENT INTAKE LINK ──
  if (screen === 'intake') {
    const intakeLink = room.intake_token ? `${window.location.origin}/intake/${room.intake_token}` : null;
    return (
      <div key="intake" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Client Intake Link" back="shop" />
        <div className="modal-scroll" style={{ flex: 1, padding: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 16 }}>
            Share this link with clients. They fill out a short damage form — no login needed — and it lands on your floor as a claim marked <b style={{ color: 'var(--orange)' }}>NEW</b>.
          </div>
          {intakeLink ? (
            <>
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-all', color: 'var(--text2)' }}>{intakeLink}</div>
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }}
                onClick={async () => {
                  try { await navigator.clipboard.writeText(intakeLink); toast('Link copied', 'success'); }
                  catch { window.prompt('Copy the link:', intakeLink); }
                }}>
                Copy Link
              </button>
              {room.role === 'admin' && (
                <>
                  <button className="btn btn-secondary" style={{ width: '100%', marginTop: 10 }} onClick={regenerateIntakeLink}>Regenerate Link</button>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>Regenerating creates a new link and permanently disables the old one.</div>
                </>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No intake link found for this room — the intake form database migration hasn't been applied yet.</div>
          )}
        </div>
      </div>
    );
  }

  // ── MEMBERS — invite code + team roster ──
  if (screen === 'members') return (
    <div key="members" className={scrCls} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title="Members" back="main" />
      <div className="modal-scroll" style={{ flex: 1, padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Invite code */}
        <div>
          <div style={{ fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: '0 4px 7px' }}>Invite Code</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--orange)', flex: 1 }}>{room.code}</span>
            <button onClick={copyCode} className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px', flexShrink: 0 }}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', margin: '6px 4px 0' }}>Coworkers join this shop by entering this code.</div>
        </div>

        {/* Members */}
        <div>
          <div style={{ fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: '0 4px 7px' }}>Members</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loadingMembers ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><div className="spinner" /></div>
            ) : members.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg4)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text2)', flexShrink: 0, overflow: 'hidden' }}>
                  {m.avatar_url
                    ? <img src={m.avatar_url} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (m.user_name || '?')[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.user_name || 'Member'}</div>
                  <div style={{ fontSize: 11, color: m.role === 'admin' ? 'var(--orange)' : 'var(--text3)', marginTop: 1, fontWeight: m.role === 'admin' ? 700 : 400 }}>{m.role}</div>
                </div>
                {room.role === 'admin' && m.user_id !== session.user.id && (
                  <>
                    <button onClick={() => changeMemberRole(m.id, m.role === 'admin' ? 'member' : 'admin')} className="raise-3d" style={{ background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 7, cursor: 'pointer', flexShrink: 0 }}>
                      {m.role === 'admin' ? 'Make Member' : 'Make Admin'}
                    </button>
                    <button onClick={() => removeMember(m.id)} disabled={removingId === m.id} className="raise-3d" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: 'var(--red)', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 7, cursor: 'pointer', flexShrink: 0 }}>
                      {removingId === m.id ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Remove'}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );

  return null;
}

// A labeled group of menu rows — rows sit in one rounded container with
// hairline separators (via .menu-group CSS); optional footnote under the group.
function MenuSection({ label, foot, children }) {
  return (
    <div>
      {label && <div style={{ fontSize: 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: '0 4px 7px' }}>{label}</div>}
      <div className="menu-group" style={{ background: 'var(--bg3)', border: 'none', borderRadius: 12, overflow: 'hidden', boxShadow: '0 3px 0 rgba(16,16,20,0.8), 0 7px 12px rgba(0,0,0,0.38)' }}>{children}</div>
      {foot && <div style={{ fontSize: 11, color: 'var(--text3)', margin: '7px 4px 0', lineHeight: 1.5 }}>{foot}</div>}
    </div>
  );
}

// One row: tinted icon tile + label; right side defaults to a chevron,
// pass right={null} to hide it or right={<node>} to replace it (e.g. a toggle).
function MenuRow({ icon, tint, label, value, right, onClick, danger }) {
  const iconBg = danger ? 'rgba(239,68,68,0.12)' : tint ? `${tint}1f` : 'var(--bg4)';
  const iconColor = danger ? 'var(--red)' : tint || 'var(--text2)';
  return (
    <div className={onClick ? 'menu-row' : undefined} onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: onClick ? 'pointer' : 'default', userSelect: 'none' }}>
      <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: iconBg, color: iconColor }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: danger ? 'var(--red)' : 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      {value && <span style={{ fontSize: 12.5, color: 'var(--text3)', flexShrink: 0, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>}
      {right !== undefined ? right : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
      )}
    </div>
  );
}

// ── PIPE BOX ──────────────────────────────────────────────────────────────────
function PipeBox({ label, count, color, active, onClick, desktop }) {
  // Stage colors are hex (#06b6d4 → glow with alpha); the "All" box uses a CSS var → neutral glow
  const glow = color.startsWith('#') ? `${color}90` : 'rgba(200, 200, 212, 0.45)';
  return (
    <div onClick={onClick} className={`pipe-3d${active ? ' pipe-3d-in' : ''}`} style={{ '--pipe-glow': glow, border: 'none', borderRadius: 10, padding: desktop ? '11px 13px 9px' : '9px 12px 8px', cursor: 'pointer', textAlign: 'center', minWidth: desktop ? 96 : 80, flexShrink: 0 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 21, fontWeight: 800, lineHeight: 1, color }}>{count}</div>
      <div style={{ fontSize: desktop ? 12.5 : 9, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text)', marginTop: 3, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ height: desktop ? 2 : 3, borderRadius: 2, marginTop: desktop ? 4 : 5, background: color, opacity: active ? 1 : 0.5 }} />
    </div>
  );
}

// ── WARNING CHIP ──────────────────────────────────────────────────────────────
function WarnChip({ w, desktop }) {
  const high = w.severity === 'high';
  const c = high ? '239,68,68' : '234,179,8';
  return (
    <span title={w.detail} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: desktop ? 11 : 10, fontWeight: 700, letterSpacing: '0.3px',
      padding: desktop ? '3px 9px' : '3px 7px', borderRadius: 6,
      background: `rgba(${c},0.15)`, color: `rgb(${c})`, border: `1px solid rgba(${c},0.4)`,
      whiteSpace: 'nowrap',
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20L12 3z"/><line x1="12" y1="10" x2="12" y2="14"/><line x1="12" y1="17.5" x2="12.01" y2="17.5"/></svg>
      {w.label}
    </span>
  );
}

// ── CLAIM CARD ────────────────────────────────────────────────────────────────
// Days since the car came in (Day 1 = the car-in day itself)
function daysInShop(claim) {
  if (!claim.car_in || claim.status === 'Closed') return null;
  const days = Math.floor((Date.now() - new Date(claim.car_in + 'T00:00:00')) / 86400000) + 1;
  return days > 0 ? days : null;
}

function ClaimCard({ claim, idx, onClick, desktop, warnings = [], unread, roomLabel }) {
  const progress = stageProgress(claim.status);
  const color = STAGE_COLORS[claim.status] || '#f97316';
  const sTheme = specialStageTheme(claim.status);
  const partsTotal = (claim.parts || []).length;
  const partsIn = (claim.parts || []).filter(p => p.arrived).length;
  const partsColor = partsTotal === 0 ? 'var(--text3)' : partsIn === partsTotal ? 'var(--green)' : 'var(--yellow)';
  const partsText = partsTotal === 0 ? 'No parts' : partsIn === partsTotal ? 'All parts in' : `Parts ${partsIn}/${partsTotal}`;
  const days = daysInShop(claim);
  function fmtDate(d) { if (!d) return '—'; const p = d.split('-'); return `${p[1]}/${p[2]}/${p[0]}`; }
  return (
    <div className="claim-card" onClick={onClick} style={{ background: sTheme ? sTheme.cardBg : 'var(--bg2)', border: sTheme ? `1px solid ${sTheme.cardBorder}` : 'none', borderRadius: 15, padding: desktop ? 20 : 12, cursor: 'pointer', animationDelay: `${idx * 0.03}s`, display: 'flex', flexDirection: 'column', height: desktop ? '100%' : undefined }}>
      {roomLabel && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start', marginBottom: desktop ? 10 : 8, padding: '3px 9px', borderRadius: 20, background: 'var(--bg4)', border: '1px solid var(--border)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text2)' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>
          {roomLabel}
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: desktop ? 12 : 9 }}>
          {warnings.map(w => <WarnChip key={w.type} w={w} desktop={desktop} />)}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: desktop ? 8 : 6 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 22 : 17, fontWeight: 700, letterSpacing: '0.3px', lineHeight: 1.15 }}>{claim.car || 'Unknown Vehicle'}</div>
          {claim.plate && <div style={{ display: 'inline-block', marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: desktop ? 12 : 10, fontWeight: 700, letterSpacing: '1.5px', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 7px', color: 'var(--text2)' }}>{claim.plate}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          {unread && <span className="unread-dot" title="Updated since you last opened it" style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0 }} />}
          {claim.unreviewed && (
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', padding: '3px 8px', borderRadius: 20, background: 'var(--orange)', color: '#16100b', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>New</span>
          )}
          <div style={{ fontSize: desktop ? 11 : 9.5, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', padding: desktop ? '4px 9px' : '3px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: `${color}22`, color, border: `1px solid ${color}44` }}>{claim.status}</div>
        </div>
      </div>
      <div style={{ height: 3, background: 'var(--bg4)', borderRadius: 2, margin: desktop ? '10px 0' : '7px 0', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      {(claim.tags || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: desktop ? 8 : 6 }}>
          {(claim.tags || []).map(tag => <span key={tag.id} className={`tag-chip ${tag.type}`}>{tag.type === 'tow' ? <IcoTow size={12} /> : tag.type === 'rental' ? <IcoRental size={12} /> : null}{tag.label}</span>)}
        </div>
      )}
      {desktop ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          {claim.client_name && <MetaItem label="Client" value={claim.client_name} desktop={desktop} />}
          {claim.insurer && <MetaItem label="Insurance" value={claim.insurer} desktop={desktop} />}
          {claim.car_in && <MetaItem label="In" value={fmtDate(claim.car_in)} desktop={desktop} />}
          {claim.car_out && <MetaItem label="Est. out" value={fmtDate(claim.car_out)} desktop={desktop} />}
        </div>
      ) : (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {(claim.client_name || claim.insurer) && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {claim.client_name && <span style={{ fontWeight: 600, color: 'var(--text)' }}>{claim.client_name}</span>}
              {claim.client_name && claim.insurer && <span style={{ color: 'var(--border)' }}>·</span>}
              {claim.insurer && <span>{claim.insurer}</span>}
            </div>
          )}
          {claim.car_in && <div style={{ color: 'var(--text3)', fontSize: 11 }}>In {fmtDate(claim.car_in)}{claim.car_out ? ` · Est. out ${fmtDate(claim.car_out)}` : ''}</div>}
        </div>
      )}
      <div style={{ marginTop: desktop ? 'auto' : 8, paddingTop: desktop ? 12 : 7, borderTop: '1px solid var(--border)', fontSize: desktop ? 12 : 10.5, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>{claim.claim_num ? `#${claim.claim_num}` : 'No claim #'}</span>
        {days && <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.5px', color: days >= 14 ? 'var(--orange)' : 'var(--text3)' }}>Day {days}</span>}
        <span style={{ color: partsColor }}>{partsText}</span>
      </div>
    </div>
  );
}

// Compact list row — plate · car · stage · day counter, for busy floors
function ClaimRow({ claim, onClick, desktop, unread, roomLabel }) {
  const color = STAGE_COLORS[claim.status] || '#f97316';
  const days = daysInShop(claim);
  const partsTotal = (claim.parts || []).length;
  const partsIn = (claim.parts || []).filter(p => p.arrived).length;
  const partsColor = partsTotal === 0 ? 'var(--text3)' : partsIn === partsTotal ? 'var(--green)' : 'var(--yellow)';
  return (
    <div className="claim-card" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: desktop ? 14 : 9, background: 'var(--bg2)', border: 'none', borderRadius: 10, padding: desktop ? '10px 16px' : '9px 11px', cursor: 'pointer' }}>
      <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: color, flexShrink: 0 }} />
      {claim.plate && <span style={{ fontFamily: 'var(--font-mono)', fontSize: desktop ? 12 : 10.5, fontWeight: 700, letterSpacing: '1px', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 7px', color: 'var(--text2)', flexShrink: 0 }}>{claim.plate}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: desktop ? 15 : 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {claim.car || 'Unknown Vehicle'}
          {roomLabel && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 10, background: 'var(--bg4)', border: '1px solid var(--border)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--text3)', whiteSpace: 'nowrap' }}>{roomLabel}</span>}
        </div>
        {desktop && claim.client_name && <div style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{claim.client_name}{claim.insurer ? ` · ${claim.insurer}` : ''}</div>}
      </div>
      {desktop && claim.claim_num && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text3)', flexShrink: 0 }}>#{claim.claim_num}</span>}
      {desktop && partsTotal > 0 && <span style={{ fontSize: 11.5, fontWeight: 600, color: partsColor, flexShrink: 0 }}>Parts {partsIn}/{partsTotal}</span>}
      {days && <span style={{ fontFamily: 'var(--font-mono)', fontSize: desktop ? 11 : 10, fontWeight: 700, letterSpacing: '0.5px', color: days >= 14 ? 'var(--orange)' : 'var(--text3)', flexShrink: 0 }}>{days} {days === 1 ? 'Day' : 'Days'}</span>}
      <span style={{ fontSize: desktop ? 10.5 : 9, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: `${color}22`, color, border: `1px solid ${color}44` }}>{stageShort(claim.status)}</span>
      {unread && <span className="unread-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0 }} />}
    </div>
  );
}

function MetaItem({ label, value, desktop }) {
  return (
    <div style={{ fontSize: desktop ? 12.5 : 11.5, color: 'var(--text3)' }}>
      <strong style={{ display: 'block', fontSize: desktop ? 14 : 13, color: 'var(--text)', fontWeight: 600, marginBottom: 1 }}>{value}</strong>
      {label}
    </div>
  );
}

// Admin dashboard: this month's numbers + all-time, computed from the claims
// already loaded on the floor. Also exports everything as a CSV file.
function ShopStats({ claims, room, onClose, desktop }) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const active = claims.filter(c => c.status !== 'Closed');
  const carsInThisMonth = claims.filter(c => c.car_in && new Date(c.car_in + 'T12:00:00') >= monthStart).length;
  const closedThisMonth = claims.filter(c => c.status === 'Closed' && new Date(c.updated_at) >= monthStart).length;
  const totalLoss = claims.filter(c => c.status === 'Total Loss').length;

  // Average days in shop, from closed claims that have both dates
  const finished = claims.filter(c => c.status === 'Closed' && c.car_in && c.car_out);
  const avgDays = finished.length
    ? Math.round(finished.reduce((sum, c) => sum + Math.max(0, (new Date(c.car_out) - new Date(c.car_in)) / 86400000), 0) / finished.length)
    : null;

  // Active cars per stage (skip empty stages)
  const byStage = STAGES.map(s => [s, active.filter(c => c.status === s).length]).filter(([, n]) => n > 0);

  // Top insurers across all claims
  const insurerCounts = {};
  for (const c of claims) { if (c.insurer) insurerCounts[c.insurer] = (insurerCounts[c.insurer] || 0) + 1; }
  const topInsurers = Object.entries(insurerCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  function exportCsv() {
    const cols = ['car', 'plate', 'color', 'vin', 'client_name', 'client_phone', 'insurer', 'claim_num', 'adjuster', 'car_in', 'car_out', 'status', 'notes', 'created_at', 'updated_at'];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [cols.join(','), ...claims.map(c => cols.map(k => esc(c[k])).join(','))];
    const blob = new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shoptrack-claims-${now.toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(`Exported ${claims.length} claims to CSV.`, 'success');
  }

  const Stat = ({ label, value, color }) => (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: desktop ? '16px 18px' : '13px 14px', textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: desktop ? 30 : 24, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: desktop ? 11.5 : 10.5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.75)' }} onClick={onClose} />
      <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 640, maxHeight: desktop ? '86vh' : undefined }}>
        {/* Header */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72, padding: '0 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>Shop Stats</div>
          <button onClick={onClose} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
        </div>

        <div className="modal-scroll" style={{ flex: 1, padding: desktop ? '20px 24px' : '16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* This month */}
          <div>
            <div className="label" style={{ marginBottom: 10 }}>{monthName}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Stat label="Cars In" value={carsInThisMonth} color="var(--blue)" />
              <Stat label="Closed" value={closedThisMonth} color="var(--green)" />
              <Stat label="On Floor Now" value={active.length} color="var(--orange)" />
            </div>
          </div>

          {/* All time */}
          <div>
            <div className="label" style={{ marginBottom: 10 }}>All Time</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Stat label="Total Claims" value={claims.length} />
              <Stat label="Avg Days In Shop" value={avgDays ?? '—'} color={avgDays > 21 ? 'var(--yellow)' : undefined} />
              <Stat label="Total Loss" value={totalLoss} color={totalLoss > 0 ? 'var(--red)' : undefined} />
            </div>
          </div>

          {/* Per stage */}
          {byStage.length > 0 && (
            <div>
              <div className="label" style={{ marginBottom: 10 }}>Cars Per Stage</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {byStage.map(([stage, n]) => {
                  const color = STAGE_COLORS[stage];
                  const pct = Math.round((n / active.length) * 100);
                  return (
                    <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: desktop ? 150 : 118, fontSize: desktop ? 12.5 : 11, color: 'var(--text2)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stage}</div>
                      <div style={{ flex: 1, height: 8, background: 'var(--bg4)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
                      </div>
                      <div style={{ width: 24, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, textAlign: 'right', flexShrink: 0 }}>{n}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top insurers */}
          {topInsurers.length > 0 && (
            <div>
              <div className="label" style={{ marginBottom: 10 }}>Top Insurers</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topInsurers.map(([name, n]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{name}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--text2)' }}>{n} claims</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer: CSV export */}
        <div style={{ padding: '14px 18px calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={exportCsv} style={{ width: '100%', padding: 13, fontSize: 14, fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase' }}>
            ⬇ Export All Claims (CSV)
          </button>
        </div>
      </div>
    </div>
  );
}

// Room-wide inbox view: threads matched to claims, unmatched threads with a
// manual assign picker, and a "Check Now" button that triggers gmail-sync.
function EmailsWindow({ room, claims, onClose, onOpenClaim, desktop }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastLog, setLastLog] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [{ data: threadRows }, { data: logRow }] = await Promise.all([
      supabase.from('email_threads')
        .select('*, claims(car, plate), email_messages(id, sender, preview_text, received_at)')
        .eq('room_id', room.id)
        .order('last_message_at', { ascending: false })
        .order('received_at', { ascending: true, foreignTable: 'email_messages' }),
      supabase.from('email_fetch_log').select('*').eq('room_id', room.id).order('fetched_at', { ascending: false }).limit(1).maybeSingle(),
    ]);
    if (threadRows) setThreads(threadRows);
    if (logRow) setLastLog(logRow);
    setLoading(false);
  }

  async function checkNow() {
    setChecking(true);
    const { data, error } = await supabase.functions.invoke('gmail-sync', { body: { room_id: room.id } });
    setChecking(false);
    if (error || data?.error) { toast(data?.error || 'Could not check email — try again.', 'error'); return; }
    toast(`${data.matched} matched, ${data.unmatched} unmatched.`, 'success');
    load();
  }

  async function assignThread(threadId, claimId) {
    const claim = claims.find(c => c.id === claimId);
    setThreads(prev => prev.map(t => (t.id === threadId ? { ...t, claim_id: claimId, claims: claim || null } : t)));
    setAssigningId(null);
    setAssignSearch('');
    const { error } = await supabase.from('email_threads').update({ claim_id: claimId }).eq('id', threadId);
    if (error) { toast('Could not assign this email — try again.', 'error'); load(); }
  }

  function fmtTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  const matched = threads.filter(t => t.claim_id);
  const unmatched = threads.filter(t => !t.claim_id);

  return (
    <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.75)' }} onClick={onClose} />
      <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 680, maxHeight: desktop ? '86vh' : undefined }}>
        {/* Header */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72, padding: '0 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>Emails</div>
          <button onClick={onClose} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
        </div>

        {/* Check Now bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: 12, color: 'var(--text3)' }}>
            {!room.connected_email
              ? 'Connect your email in Room Settings to start checking for claim emails'
              : lastLog ? `Last check: ${fmtTime(lastLog.fetched_at)} — ${lastLog.matched} matched, ${lastLog.unmatched} unmatched` : 'No checks yet'}
          </div>
          <button onClick={checkNow} disabled={checking || !room.connected_email} className="btn btn-primary" style={{ padding: '7px 16px', fontSize: 13, flexShrink: 0, opacity: room.connected_email ? 1 : 0.4 }}>
            {checking ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Check Now'}
          </button>
        </div>

        <div className="modal-scroll" style={{ flex: 1, padding: desktop ? '16px 20px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : !room.connected_email ? (
            <div className="empty-state">
              <div className="icon">📧</div>
              <div className="title">No email connected</div>
              <div className="sub">Ask an admin to connect the shop's Gmail in Room Settings</div>
            </div>
          ) : threads.length === 0 ? (
            <div className="empty-state">
              <div className="icon">📧</div>
              <div className="title">No emails yet</div>
              <div className="sub">Tap Check Now to pull the last 24 hours</div>
            </div>
          ) : (
            <>
              {matched.length > 0 && (
                <div>
                  <div className="label" style={{ marginBottom: 10 }}>Matched to Claims</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {matched.map(t => (
                      <ThreadRow key={t.id} thread={t} expanded={expandedId === t.id} onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)} onOpenClaim={onOpenClaim} fmtTime={fmtTime} />
                    ))}
                  </div>
                </div>
              )}
              {unmatched.length > 0 && (
                <div>
                  <div className="label" style={{ marginBottom: 10 }}>Unmatched ({unmatched.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {unmatched.map(t => (
                      <ThreadRow key={t.id} thread={t} expanded={expandedId === t.id} onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)} onOpenClaim={onOpenClaim} fmtTime={fmtTime}
                        extra={
                          assigningId === t.id ? (
                            <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                              <input autoFocus value={assignSearch} onChange={e => setAssignSearch(e.target.value)} placeholder="Search claims by plate or car..." style={{ marginBottom: 6, fontSize: 13 }} />
                              <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {claims.filter(c => {
                                  const q = assignSearch.toLowerCase();
                                  return !q || (c.car || '').toLowerCase().includes(q) || (c.plate || '').toLowerCase().includes(q) || (c.claim_num || '').toLowerCase().includes(q);
                                }).slice(0, 20).map(c => (
                                  <button key={c.id} onClick={() => assignThread(t.id, c.id)} style={{ textAlign: 'left', padding: '8px 10px', background: 'var(--bg4)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                                    {c.car || 'Unknown'}{c.plate ? ` · ${c.plate}` : ''}{c.claim_num ? ` · #${c.claim_num}` : ''}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); setAssigningId(t.id); setAssignSearch(''); }} className="btn btn-secondary" style={{ marginTop: 8, fontSize: 12, padding: '5px 12px' }}>Assign to claim</button>
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadRow({ thread, expanded, onToggle, onOpenClaim, fmtTime, extra }) {
  const messages = thread.email_messages || [];
  const claim = thread.claims;
  const last = messages[messages.length - 1];
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 14px' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread.subject || '(no subject)'}</div>
          {claim && (
            <button onClick={e => { e.stopPropagation(); onOpenClaim(thread.claim_id); }} style={{ marginTop: 3, background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600, color: 'var(--orange)', cursor: 'pointer' }}>
              {claim.car || 'Claim'}{claim.plate ? ` · ${claim.plate}` : ''}
            </button>
          )}
          {last && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{last.preview_text}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtTime(thread.last_message_at)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{messages.length} msg{messages.length !== 1 ? 's' : ''}</div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.map(m => (
            <div key={m.id} style={{ fontSize: 12.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.sender}</span>
                <span style={{ color: 'var(--text3)', flexShrink: 0 }}>{fmtTime(m.received_at)}</span>
              </div>
              <div style={{ color: 'var(--text3)', marginTop: 2 }}>{m.preview_text}</div>
            </div>
          ))}
          {thread.gmail_thread_id && (
            <a href={`https://mail.google.com/mail/u/0/#inbox/${thread.gmail_thread_id}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--blue)', textDecoration: 'none' }}>
              Open in Gmail →
            </a>
          )}
        </div>
      )}
      {extra}
    </div>
  );
}

function ImportantContacts({ room, session, onClose, desktop }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { loadContacts(); }, []);

  async function loadContacts() {
    const { data } = await supabase.from('contacts').select('*').eq('room_id', room.id).order('created_at', { ascending: true });
    if (data) setContacts(data);
    setLoading(false);
  }

  async function addContact() {
    if (!name.trim() || !phone.trim()) return;
    // Optimistic: show the contact and clear the form immediately
    const temp = { id: `temp-${Date.now()}`, room_id: room.id, name: name.trim(), phone: phone.trim() };
    const prevName = name, prevPhone = phone;
    setContacts(prev => [...prev, temp]);
    setName(''); setPhone('');
    const { data, error } = await supabase.from('contacts').insert({ room_id: room.id, name: temp.name, phone: temp.phone }).select().single();
    if (error) {
      setContacts(prev => prev.filter(c => c.id !== temp.id));
      setName(prevName); setPhone(prevPhone);
      toast('Could not add the contact — check your connection and try again.', 'error');
      return;
    }
    setContacts(prev => prev.map(c => (c.id === temp.id ? data : c)));
  }

  async function deleteContact(id) {
    if (!window.confirm('Delete this contact?')) return;
    // Shrink the row away, then remove it
    setDeletingId(id);
    await new Promise(r => setTimeout(r, 180));
    setDeletingId(null);
    const removed = contacts.find(c => c.id === id);
    // Optimistic: remove immediately, restore if the delete fails
    setContacts(prev => prev.filter(c => c.id !== id));
    const { error } = await supabase.from('contacts').delete().eq('id', id);
    if (error && removed) {
      setContacts(prev => [...prev, removed]);
      toast('Could not delete the contact — check your connection and try again.', 'error');
    }
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.7)' }} onClick={onClose} />
      <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 520, maxHeight: desktop ? '80vh' : undefined }}>
        {/* Header */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72, padding: '0 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>Contacts</div>
          <button onClick={onClose} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
        </div>

        {/* Add form */}
        <div style={{ padding: '14px 16px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0, display: 'flex', gap: 8 }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" style={{ flex: 1 }} />
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && addContact()} />
          <button onClick={addContact} disabled={saving || !name.trim() || !phone.trim()} className="btn btn-primary" style={{ padding: '0 16px', flexShrink: 0, fontSize: 14 }}>
            {saving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '+ Add'}
          </button>
        </div>

        {/* List */}
        <div className="modal-scroll" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>
          ) : contacts.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 20px' }}>
              <div className="icon">📋</div>
              <div className="title">No contacts yet</div>
              <div className="sub">Add names and numbers above</div>
            </div>
          ) : contacts.map(c => (
            <div key={c.id} className={deletingId === c.id ? 'removing' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--orange-dim)', border: '1px solid var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: 'var(--orange)', flexShrink: 0 }}>
                {c.name[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{c.name}</div>
                <a href={`tel:${c.phone}`} style={{ color: 'var(--green)', fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 600, textDecoration: 'none' }}>{c.phone}</a>
              </div>
              <button onClick={() => deleteContact(c.id)} disabled={deletingId === c.id} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: 'var(--red)', fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', flexShrink: 0 }}>
                {deletingId === c.id ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InsuranceSearch({ onSearch }) {
  const [val, setVal] = useState('');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 12px', height: 40 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg>
      <input value={val} onChange={e => { setVal(e.target.value); onSearch(e.target.value); }} placeholder="Search insurance..." style={{ background: 'none', border: 'none', padding: '0', fontSize: 14, color: 'var(--text)', width: '100%' }} />
      {val && <button onClick={() => { setVal(''); onSearch(''); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer', padding: '0 2px' }}>×</button>}
    </div>
  );
}

function stageShort(s) {
  const map = { 'Appraiser Inspection': 'Inspection', 'Waiting on Approval': 'Wait Approval', 'Supplement Requested': 'Supp. Req.', 'Waiting on Supplement': 'Wait Supp.', 'Waiting on Parts': 'Wait Parts' };
  return map[s] || s;
}
